import { Fragment, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from "react-bootstrap";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import OfficeGoogleMapPicker from "./OfficeGoogleMapPicker";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const weekDays = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];
const attendanceStatuses = ["", "P", "HD", "A", "L", "WO", "H"];
const statusLabels = { P: "Present", HD: "Half Day", A: "Absent", L: "Paid Leave", WO: "Weekly Off", H: "Holiday" };
const gridStatusVariant = { P: "success", HD: "warning", A: "danger", L: "info", WO: "secondary", H: "primary" };
const punchStatusVariant = { PRESENT: "success", HALF_DAY: "warning", ABSENT: "danger", LEAVE: "info", WEEKLY_OFF: "secondary", HOLIDAY: "primary", MANUAL: "dark" };
const blankConfig = {
  officeStartTime: "09:30",
  officeEndTime: "18:30",
  graceMinutes: 15,
  fullDayMinutes: 480,
  halfDayMinutes: 240,
  weekendDays: [0],
  salaryCalculationBasis: "CALENDAR_DAYS",
  halfDayPayRatio: 0.5,
  paidLeave: true,
  paidWeekend: true,
  paidHoliday: true,
  autoHalfDayFromPunchHours: true,
  autoAbsentForShortHours: false,
  officeLocations: [],
  officeLocation: null,
  allowedRadiusMeters: 0,
};

const blankSalary = { basic: "", da: "", hra: "", medical: "", conveyance: "" };

const blankCompliance = {
  pfApplicable: false,
  pfNumber: "",
  uan: "",
  pfRate: 12,
  esicApplicable: false,
  esicNumber: "",
  esicRate: 0.75,
  ptApplicable: false,
  ptState: "",
  tdsApplicable: false,
  tdsType: "FIXED",
  tdsAmount: "",
  tdsRate: "",
  wcApplicable: false,
  wcPolicyNumber: "",
  wcInsuranceVendor: "",
};

const getEmployeeId = (record) => {
  const employee = record?.employeeId;
  return employee ? String(employee._id || employee) : "";
};
const getPunchUserId = (record) => {
  const user = record?.userId || record?.employeeId?.userId;
  return user ? String(user._id || user) : "";
};
const formatMoney = (value) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(value || 0));
const formatExportMoney = (value) => `INR ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const safeText = (value) => String(value ?? "-").replace(/[\u20B9]/g, "INR").replace(/[^\x20-\x7E]/g, " ");
const sanitizeFileName = (value) => String(value || "export").replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
const truncateText = (value, max = 24) => {
  const text = safeText(value);
  return text.length > max ? `${text.slice(0, Math.max(0, max - 3))}...` : text;
};
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
const formatDate = (value) => {
  if (!value) return "-";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const formatDuration = (minutes) => {
  const total = Number(minutes || 0);
  if (!total) return "-";
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours}h ${mins}m`;
};

const formatLocation = (location) => {
  if (!location?.latitude || !location?.longitude) return "-";
  if (location.address) return location.address;
  return `${Number(location.latitude).toFixed(5)}, ${Number(location.longitude).toFixed(5)}`;
};

const getMapUrl = (location) => {
  if (!location?.latitude || !location?.longitude) return "";
  return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
};

const getDefaultLogDay = (targetMonth = new Date().getMonth(), targetYear = new Date().getFullYear()) => {
  const today = new Date();
  return Number(targetMonth) === today.getMonth() && Number(targetYear) === today.getFullYear()
    ? String(today.getDate())
    : "";
};

// Map Preview Modal 
const MapPreviewModal = ({ show, onHide, record }) => {
  const hasIn = Boolean(record?.locationIn?.latitude && record?.locationIn?.longitude);
  const hasOut = Boolean(record?.locationOut?.latitude && record?.locationOut?.longitude);

  const embedUrl = (() => {
    if (!MAPS_API_KEY) return "";
    if (hasIn && hasOut) {
      const origin = `${record.locationIn.latitude},${record.locationIn.longitude}`;
      const dest = `${record.locationOut.latitude},${record.locationOut.longitude}`;
      return `https://www.google.com/maps/embed/v1/directions?key=${MAPS_API_KEY}&origin=${origin}&destination=${dest}&mode=driving`;
    }
    const loc = hasIn ? record.locationIn : record?.locationOut;
    if (!loc) return "";
    return `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${loc.latitude},${loc.longitude}&zoom=16`;
  })();

  const dateLabel = record?.dateKey
    ? new Date(`${record.dateKey}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  return (
    <>
      {/* Custom fade overlay that sits between the two modals */}
      {show && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1055,
            background: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            transition: "opacity 0.2s ease",
          }}
          onClick={onHide}
        />
      )}

      <Modal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        backdrop={false}
        style={{ zIndex: 1060 }}
      >
        <Modal.Header closeButton style={{ padding: "12px 18px" }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Location Map & Punch Photos — {dateLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Row className="g-0">
            {/* Left Column: Punch In & Punch Out Details with Proper Photos */}
            <Col md={5} className="p-3 bg-light border-end d-flex flex-column gap-3" style={{ maxHeight: 460, overflowY: "auto" }}>
              {/* Punch In Card */}
              <div className="p-2 bg-white rounded border shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1" style={{ fontSize: 11 }}>
                    ● PUNCH IN
                  </span>
                  <strong style={{ fontSize: 14 }}>{formatTime(record?.punchInAt)}</strong>
                </div>

                {record?.photoIn ? (
                  <div className="mb-2 text-center">
                    <a href={getFileUrl(record.photoIn)} target="_blank" rel="noreferrer" title="Click to view full photo">
                      <img
                        src={getFileUrl(record.photoIn)}
                        alt="Punch In Selfie"
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        }}
                      />
                    </a>
                    <div className="text-muted mt-1" style={{ fontSize: 10 }}>Click image to expand</div>
                  </div>
                ) : (
                  <div className="text-center py-3 mb-2 rounded bg-light border text-muted small">
                    No In photo captured
                  </div>
                )}

                <div className="small text-muted" style={{ fontSize: 11, lineHeight: 1.3 }}>
                  <div className="fw-semibold text-dark mb-0.5">Coordinates:</div>
                  {hasIn ? (
                    <div>{Number(record.locationIn.latitude).toFixed(5)}, {Number(record.locationIn.longitude).toFixed(5)}</div>
                  ) : (
                    <div>No GPS coordinates</div>
                  )}
                  {record?.locationIn?.address && (
                    <div className="mt-1 text-truncate" title={record.locationIn.address}>
                      {record.locationIn.address}
                    </div>
                  )}
                </div>
              </div>

              {/* Punch Out Card */}
              <div className="p-2 bg-white rounded border shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="badge bg-danger-subtle text-danger border border-danger fw-bold px-2 py-1" style={{ fontSize: 11 }}>
                    ● PUNCH OUT
                  </span>
                  <strong style={{ fontSize: 14 }}>{formatTime(record?.punchOutAt)}</strong>
                </div>

                {record?.photoOut ? (
                  <div className="mb-2 text-center">
                    <a href={getFileUrl(record.photoOut)} target="_blank" rel="noreferrer" title="Click to view full photo">
                      <img
                        src={getFileUrl(record.photoOut)}
                        alt="Punch Out Selfie"
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        }}
                      />
                    </a>
                    <div className="text-muted mt-1" style={{ fontSize: 10 }}>Click image to expand</div>
                  </div>
                ) : (
                  <div className="text-center py-3 mb-2 rounded bg-light border text-muted small">
                    No Out photo captured
                  </div>
                )}

                <div className="small text-muted" style={{ fontSize: 11, lineHeight: 1.3 }}>
                  <div className="fw-semibold text-dark mb-0.5">Coordinates:</div>
                  {hasOut ? (
                    <div>{Number(record.locationOut.latitude).toFixed(5)}, {Number(record.locationOut.longitude).toFixed(5)}</div>
                  ) : (
                    <div>No GPS coordinates</div>
                  )}
                  {record?.locationOut?.address && (
                    <div className="mt-1 text-truncate" title={record.locationOut.address}>
                      {record.locationOut.address}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* Right Column: Google Maps Iframe */}
            <Col md={7} className="d-flex flex-column">
              {embedUrl ? (
                <iframe
                  title="Punch Location Map"
                  src={embedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 460, display: "block" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="text-center text-muted d-flex align-items-center justify-content-center flex-grow-1" style={{ minHeight: 350 }}>
                  No GPS map data available
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between">
          {(hasIn || hasOut) ? (
            <a
              href={`https://www.google.com/maps?q=${(hasIn ? record.locationIn : record.locationOut).latitude},${(hasIn ? record.locationIn : record.locationOut).longitude}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-primary"
            >
              Open in Google Maps ↗
            </a>
          ) : <div />}
          <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


const StaffSalaryOverview = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [attendance, setAttendance] = useState({});
  const [punches, setPunches] = useState([]);
  const [punchMap, setPunchMap] = useState({});
  const [config, setConfig] = useState(blankConfig);
  const [loading, setLoading] = useState(false);
  const [mapRecord, setMapRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [exporting, setExporting] = useState("");
  const [search, setSearch] = usePersistentState("staff_sal_search", "");
  const [logDay, setLogDay] = useState(() => getDefaultLogDay());
  const [month, setMonth] = usePersistentState("staff_sal_month", new Date().getMonth());
  const [year, setYear] = usePersistentState("staff_sal_year", new Date().getFullYear());
  const [showEnroll, setShowEnroll] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [enrollingUserId, setEnrollingUserId] = useState(null);
  const [enrollSearch, setEnrollSearch] = useState("");
  const [clientMeta, setClientMeta] = useState({ emails: new Set(), mobiles: new Set(), names: new Set() });
  const [showConfig, setShowConfig] = useState(false);
  const [selectedViewStaff, setSelectedViewStaff] = useState(null);
  const [processingId, setProcessingId] = useState(null); // employeeId being processed
  const [processedMap, setProcessedMap] = useState({}); // employeeId → payroll record
  const [showLocationExportModal, setShowLocationExportModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [currentPayrollEmp, setCurrentPayrollEmp] = useState(null);
  const [payrollSalary, setPayrollSalary] = useState(blankSalary);
  const [payrollCompliance, setPayrollCompliance] = useState(blankCompliance);
  const [savingPayroll, setSavingPayroll] = useState(false);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [locationExportConfig, setLocationExportConfig] = useState({
    scope: "TODAY",
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    staffId: "",
    includePunchIn: true,
    includePunchOut: true,
    includeGps: true,
  });

  const payrollGross = ["basic", "da", "hra", "medical", "conveyance"]
    .reduce((sum, f) => sum + Number(payrollSalary[f] || 0), 0);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dayIndexes = useMemo(() => Array.from({ length: daysInMonth }, (_, index) => index), [daysInMonth]);
  const calendarCells = useMemo(() => {
    const firstWeekDay = new Date(year, month, 1).getDay();
    const cells = [];
    for (let index = 0; index < firstWeekDay; index += 1) cells.push(null);
    dayIndexes.forEach((day) => cells.push(day));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [dayIndexes, month, year]);

  const enrolledUserIds = useMemo(() => new Set(staff.map((item) => item.userId?._id).filter(Boolean)), [staff]);

  const isClientUser = (user, meta) => {
    if (!user) return false;
    const roleName = String(
      user.role?.name || (typeof user.role === "string" ? user.role : "") || user.roleName || ""
    ).toLowerCase().trim();
    if (
      roleName.includes("client") ||
      roleName.includes("customer") ||
      roleName === "po" ||
      roleName === "vendor"
    ) {
      return true;
    }
    if (user.isClient || user.isCustomer || user.isVendor) return true;
    const userType = String(user.userType || user.type || "").toLowerCase().trim();
    if (userType.includes("client") || userType.includes("customer") || userType.includes("vendor")) {
      return true;
    }
    const nameLower = String(user.name || "").toLowerCase().trim();
    if (
      nameLower.startsWith("client ") ||
      nameLower.endsWith(" client") ||
      nameLower.includes(" client ") ||
      nameLower.includes("client") ||
      nameLower.includes("(client)") ||
      nameLower.includes("[client]")
    ) {
      return true;
    }
    const emailLower = String(user.email || "").toLowerCase().trim();
    if (emailLower && meta?.emails?.has(emailLower)) return true;
    const rawMobile = String(user.mobile || "").replace(/\D/g, "");
    if (rawMobile) {
      const last10 = rawMobile.slice(-10);
      if (meta?.mobiles?.has(last10)) return true;
    }
    if (nameLower && meta?.names?.has(nameLower)) return true;
    return false;
  };

  const availableUsers = useMemo(() => {
    return users.filter((user) => {
      if (enrolledUserIds.has(user._id)) return false;
      if (isClientUser(user, clientMeta)) return false;
      return true;
    });
  }, [users, enrolledUserIds, clientMeta]);

  const filteredAvailableUsers = useMemo(() => {
    const q = enrollSearch.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const mobile = (u.mobile || "").toLowerCase();
      return name.includes(q) || email.includes(q) || mobile.includes(q);
    });
  }, [availableUsers, enrollSearch]);

  useEffect(() => { loadOverview(); }, [month, year]);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const [salaryRes, attendanceRes, punchRes, configRes] = await Promise.allSettled([
        api.get("/staff-salary/overview", { params: { month, year, search } }),
        api.get(`/staff-attendance/monthly/${month}/${year}`),
        api.get("/staff-attendance/punches", { params: { month, year } }),
        api.get("/staff-attendance/config"),
      ]);

      const salaryOk = salaryRes.status === "fulfilled" && salaryRes.value.data.success;
      const staffList = salaryOk ? salaryRes.value.data.data || [] : [];
      if (salaryOk) setSummary(salaryRes.value.data.summary || {});
      else {
        setSummary({});
        swal("Error", "Failed to load staff salary overview", "error");
      }

      const attendanceRecords = attendanceRes.status === "fulfilled" ? attendanceRes.value.data.data || [] : [];
      const punchRecords = punchRes.status === "fulfilled" ? punchRes.value.data.data || [] : [];
      let effectiveConfig = config;
      if (configRes.status === "fulfilled" && configRes.value.data.success) {
        const data = configRes.value.data.data || {};
        let officeLocations = Array.isArray(data.officeLocations) && data.officeLocations.length > 0
          ? data.officeLocations
          : [];
        if (!officeLocations.length && data.officeLocation?.latitude) {
          officeLocations = [{
            name: data.officeLocation.address || "Main Office",
            latitude: data.officeLocation.latitude,
            longitude: data.officeLocation.longitude,
            address: data.officeLocation.address || "",
            allowedRadiusMeters: Number(data.allowedRadiusMeters || 100),
          }];
        }
        effectiveConfig = { ...blankConfig, ...data, officeLocations, paidLeave: true };
        setConfig(effectiveConfig);
      }

      const recordMap = new Map(attendanceRecords.map((record) => [record.employeeId, record]));
      const nextPunchMap = {};
      punchRecords.forEach((record) => {
        const employeeId = getEmployeeId(record);
        const userId = getPunchUserId(record);
        if (!record.dateKey) return;
        const day = Number(record.dateKey.split("-")[2]) - 1;
        [employeeId, userId].filter(Boolean).forEach((id) => {
          nextPunchMap[`${id}-${day}`] = record;
        });
      });

      const weekendSet = new Set((effectiveConfig.weekendDays || []).map(Number));
      const grid = {};
      staffList.forEach((item) => {
        const record = recordMap.get(item._id);
        grid[item._id] = record?.days?.length ? record.days.slice(0, daysInMonth) : Array(daysInMonth).fill("");
        while (grid[item._id].length < daysInMonth) grid[item._id].push("");
        grid[item._id] = grid[item._id].map((status, index) => (
          weekendSet.has(new Date(year, month, index + 1).getDay()) ? "WO" : status
        ));
      });

      setStaff(staffList);
      setAttendance(grid);
      setPunches(punchRecords);
      setPunchMap(nextPunchMap);
      setSelectedViewStaff((current) => current ? staffList.find((item) => item._id === current._id) || null : null);
      // load processed payroll statuses
      loadProcessedStatuses(staffList, month, year);
    } catch (error) {
      swal("Error", "Failed to load staff salary overview", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const [usersRes, clientsRes] = await Promise.allSettled([
        api.get("/users?limit=1000"),
        api.get("/clients?limit=1000"),
      ]);

      if (clientsRes.status === "fulfilled" && clientsRes.value.data) {
        const clientList = Array.isArray(clientsRes.value.data)
          ? clientsRes.value.data
          : (clientsRes.value.data.data || clientsRes.value.data.clients || []);
        const emails = new Set();
        const mobiles = new Set();
        const names = new Set();

        clientList.forEach((c) => {
          if (c.gmail) emails.add(String(c.gmail).toLowerCase().trim());
          if (c.email) emails.add(String(c.email).toLowerCase().trim());
          if (c.name) names.add(String(c.name).toLowerCase().trim());
          if (c.mobile) {
            const m = String(c.mobile).replace(/\D/g, "").slice(-10);
            if (m) mobiles.add(m);
          }
          if (Array.isArray(c.contactPersons)) {
            c.contactPersons.forEach((cp) => {
              if (cp.email) emails.add(String(cp.email).toLowerCase().trim());
              if (cp.name) names.add(String(cp.name).toLowerCase().trim());
              if (cp.mobile) {
                const m = String(cp.mobile).replace(/\D/g, "").slice(-10);
                if (m) mobiles.add(m);
              }
            });
          }
        });

        setClientMeta({ emails, mobiles, names });
      }

      if (usersRes.status === "fulfilled" && usersRes.value.data?.success) {
        setUsers(usersRes.value.data.data || []);
      }
    } catch (error) {
      swal("Error", "Failed to load users", "error");
    } finally {
      setLoadingUsers(false);
    }
  };

  const openEnroll = async () => {
    setShowEnroll(true);
    setEnrollSearch("");
    await loadUsers();
  };

  const enrollUser = async (user) => {
    try {
      setEnrollingUserId(user._id);
      const res = await api.post("/employees/payroll-staff/enroll", { userId: user._id, skills: [], level: "Junior", dailyCapacityHours: 8 });
      const employee = res.data.employee;
      const newStaff = {
        _id: employee._id,
        userId: user,
        name: user.name,
        email: user.email || "",
        mobile: user.mobile || "",
        attendance: { present: 0, absent: 0, leave: 0, wo: 0, holiday: 0, halfDay: 0 },
        salary: null,
        gross: 0,
      };
      setShowEnroll(false);
      await loadOverview();
      swal("Enrolled", `${user.name} has been enrolled in payroll successfully.`, "success");
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to enroll staff", "error");
    } finally {
      setEnrollingUserId(null);
    }
  };

  const unenrollUser = async (emp) => {
    try {
      const willUnenroll = await swal({
        title: `Unenroll ${emp.name}?`,
        text: `Are you sure you want to remove "${emp.name}" from Payroll Attendance & Salary roster?\n\nThey will not be deleted from the system and can be re-enrolled anytime from "Enroll Staff".`,
        icon: "warning",
        buttons: ["Cancel", "Yes, Unenroll"],
        dangerMode: true,
      });

      if (!willUnenroll) return;

      setLoading(true);
      const res = await api.post("/employees/payroll-staff/unenroll", {
        employeeId: emp._id,
        userId: emp.userId?._id || emp.userId,
      });

      if (res.data?.success) {
        swal("Unenrolled!", `"${emp.name}" has been removed from payroll roster.`, "success");
        await loadOverview();
      } else {
        swal("Error", res.data?.message || "Failed to unenroll staff", "error");
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to unenroll staff", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAttendanceView = (item) => {
    setLogDay(getDefaultLogDay(month, year));
    setSelectedViewStaff(item);
  };

  const openPayrollEdit = async (emp) => {
    setCurrentPayrollEmp(emp);
    setPayrollSalary(blankSalary);
    setPayrollCompliance(blankCompliance);
    setShowPayrollModal(true);
    setLoadingPayroll(true);

    try {
      const [salRes, compRes] = await Promise.allSettled([
        api.get(`/staff-salary/${emp._id}/${month}/${year}`),
        api.get(`/compliance/${emp._id}/null`),
      ]);

      if (salRes.status === "fulfilled" && salRes.value.data?.record) {
        const r = salRes.value.data.record;
        setPayrollSalary({
          basic: r.basic || "",
          da: r.da || "",
          hra: r.hra || "",
          medical: r.medical || "",
          conveyance: r.conveyance || "",
        });
      }

      if (compRes.status === "fulfilled" && compRes.value.data?.record) {
        setPayrollCompliance({ ...blankCompliance, ...compRes.value.data.record });
      }
    } catch (err) {
      console.log("Failed loading payroll data", err);
    } finally {
      setLoadingPayroll(false);
    }
  };

  const handleSavePayroll = async () => {
    if (!currentPayrollEmp) return;
    setSavingPayroll(true);
    try {
      await api.post(`/staff-salary/${currentPayrollEmp._id}/${month}/${year}`, payrollSalary);
      await api.post(`/compliance/${currentPayrollEmp._id}/null`, payrollCompliance);

      swal("Saved!", "Payroll settings updated successfully", "success");
      setShowPayrollModal(false);
      await loadOverview();
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to save payroll settings", "error");
    } finally {
      setSavingPayroll(false);
    }
  };

  // Load processed payroll status for all staff this month
  const loadProcessedStatuses = async (staffList, m, y) => {
    try {
      const res = await api.get("/staff-payroll", { params: { month: m, year: y } });
      const map = {};
      (res.data.data || []).forEach((r) => {
        const eid = r.employeeId?._id || r.employeeId;
        if (eid) map[String(eid)] = r;
      });
      setProcessedMap(map);
    } catch {
      // non-critical — ignore
    }
  };

  const processSalary = async (item) => {
    if (!item.salary) {
      swal("Warning", "Salary not configured for this staff. Please set up salary in Payroll Settings first.", "warning");
      return;
    }
    const confirm = await swal({
      title: "Process Salary?",
      text: `Process ${item.name}'s salary for ${months[month]} ${year}?`,
      icon: "info",
      buttons: ["Cancel", "Process"],
    });
    if (!confirm) return;

    setProcessingId(item._id);
    try {
      await api.post("/staff-payroll/process", {
        employeeId: item._id,
        month,
        year,
      });
      swal("Done!", "Salary processed successfully. Accounts team can now mark it as paid.", "success");
      await loadProcessedStatuses(staff, month, year);
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to process salary", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const isWeekendIndex = (dayIndex) => (config.weekendDays || []).map(Number).includes(new Date(year, month, dayIndex + 1).getDay());

  const applyWeekendStatus = (days) => days.map((status, index) => (isWeekendIndex(index) ? "WO" : status));

  const cycleStatus = (employeeId, dayIndex) => {
    if (isWeekendIndex(dayIndex)) return;
    setAttendance((prev) => {
      const days = [...(prev[employeeId] || Array(daysInMonth).fill(""))];
      const currentIndex = attendanceStatuses.indexOf(days[dayIndex]);
      days[dayIndex] = attendanceStatuses[(currentIndex + 1) % attendanceStatuses.length];
      return { ...prev, [employeeId]: applyWeekendStatus(days) };
    });
  };

  const saveSelectedAttendance = async () => {
    if (!selectedViewStaff?._id) return;
    try {
      setSaving(true);
      await api.post("/staff-attendance/monthly/save", {
        employeeId: selectedViewStaff._id,
        month,
        year,
        days: applyWeekendStatus(attendance[selectedViewStaff._id] || Array(daysInMonth).fill("")),
      });
      swal("Saved", "Staff attendance saved successfully", "success");
      await loadOverview();
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to save staff attendance", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSavingConfig(true);
      const res = await api.put("/staff-attendance/config", { ...config, paidLeave: true });
      if (res.data.success) {
        const data = res.data.data || {};
        const officeLocations = Array.isArray(data.officeLocations) ? data.officeLocations : [];
        setConfig({ ...blankConfig, ...data, officeLocations, paidLeave: true });
      }
      swal("Saved", "Attendance settings saved successfully", "success");
      setShowConfig(false);
      loadOverview();
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to save attendance settings", "error");
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleWeekend = (day) => {
    const current = new Set((config.weekendDays || []).map(Number));
    if (current.has(day)) current.delete(day);
    else current.add(day);
    setConfig({ ...config, weekendDays: [...current].sort() });
  };


  const addOfficeLocation = () => {
    const list = config.officeLocations || [];
    const newOffice = {
      name: `Office ${list.length + 1}`,
      latitude: "",
      longitude: "",
      address: "",
      allowedRadiusMeters: 100,
    };
    setConfig((current) => ({
      ...current,
      officeLocations: [...(current.officeLocations || []), newOffice],
    }));
  };

  const updateOfficeItem = (index, field, value) => {
    setConfig((current) => {
      const list = [...(current.officeLocations || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, officeLocations: list };
    });
  };

  const removeOfficeItem = (index) => {
    setConfig((current) => {
      const list = [...(current.officeLocations || [])];
      list.splice(index, 1);
      return { ...current, officeLocations: list };
    });
  };

  const [activeMapIndex, setActiveMapIndex] = useState(null);
  const [locatingOfficeIndex, setLocatingOfficeIndex] = useState(null);

  const handleCaptureLocationForItem = (index) => {
    if (!navigator.geolocation) {
      swal("Error", "Location is not supported in this browser", "error");
      return;
    }

    setLocatingOfficeIndex(index);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const uLat = Number(position.coords.latitude).toFixed(7);
        const uLng = Number(position.coords.longitude).toFixed(7);
        updateOfficeItem(index, "latitude", uLat);
        updateOfficeItem(index, "longitude", uLng);

        // Try reverse geocode if address empty
        const office = (config.officeLocations || [])[index];
        if (!office?.address && window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: Number(uLat), lng: Number(uLng) } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              updateOfficeItem(index, "address", results[0].formatted_address);
            }
            setLocatingOfficeIndex(null);
          });
        } else {
          setLocatingOfficeIndex(null);
        }
      },
      () => {
        setLocatingOfficeIndex(null);
        swal("Error", "Allow location permission to set office location", "error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };



  const filteredStaff = staff.filter((item) => {
    const keyword = search.toLowerCase();
    if (!keyword) return true;
    return item.name?.toLowerCase().includes(keyword) || item.email?.toLowerCase().includes(keyword) || item.mobile?.toLowerCase().includes(keyword);
  });

  const exportRows = useMemo(() => filteredStaff.map((item, index) => ({
    "#": index + 1,
    Staff: item.name || "-",
    Email: item.email || "-",
    Mobile: item.mobile || "-",
    Level: item.level || "Staff",
    Present: item.attendance?.present || 0,
    "Half Day": item.attendance?.halfDay || 0,
    Absent: item.attendance?.absent || 0,
    "Paid Leave": item.attendance?.leave || 0,
    "Weekly Off": item.attendance?.wo || 0,
    Holiday: item.attendance?.holiday || 0,
    "Gross Salary": Number(item.gross || 0),
    Payable: Number(item.payroll?.payableAmount || 0),
    Deduction: Number(item.payroll?.deductionAmount || 0),
    "Per Day": Number(item.payroll?.perDaySalary || 0),
    "Paid Days": Number(item.payroll?.paidDays || 0),
    "Salary Days": Number(item.payroll?.salaryDays || 0),
    Status: item.salary ? "Configured" : "Pending",
  })), [filteredStaff]);

  const getExportFileBase = () => sanitizeFileName(`Staff_Salary_Overview_${months[month]}_${year}`);

  const handleExportExcel = async () => {
    try {
      setExporting("excel");
      const XLSX = await import("xlsx-js-style");
      const wb = XLSX.utils.book_new();

      const periodLabel = `${months[month]} ${year}`;
      const exportDateStr = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

      const borderThin = {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "thin", color: { rgb: "D1D5DB" } },
      };

      const titleStyle = {
        font: { name: "Calibri", sz: 14, bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1E3A8A" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const subTitleStyle = {
        font: { name: "Calibri", sz: 10, italic: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2563EB" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const kpiHeaderStyle = {
        font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "4B5563" } },
        fill: { fgColor: { rgb: "F3F4F6" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderThin,
      };

      const kpiValueStyle = {
        font: { name: "Calibri", sz: 11, bold: true, color: { rgb: "111827" } },
        fill: { fgColor: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderThin,
      };

      const thStyle = {
        font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1E3A8A" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: borderThin,
      };

      const wsData = [
        ["CANSCORP CONSTRUCTIONS — STAFF SALARY OVERVIEW"],
        [`Period: ${periodLabel} | Generated on: ${exportDateStr}`],
        [],
        ["Total Staff", "Configured", "Total Gross Salary", "Total Payable Amount"],
        [
          summary.staffCount || filteredStaff.length || 0,
          summary.salaryConfigured || 0,
          Number(summary.totalGross || 0),
          Number(summary.totalPayable || 0),
        ],
        [],
        [
          "#",
          "Staff Name",
          "Email",
          "Mobile",
          "Level / Role",
          "Present (P)",
          "Half Day (HD)",
          "Absent (A)",
          "Paid Leave (PL)",
          "Paid Days",
          "Gross Salary",
          "Deductions",
          "Net Payable",
          "Status",
        ],
      ];

      let totalGrossSum = 0;
      let totalDeductSum = 0;
      let totalPayableSum = 0;

      filteredStaff.forEach((item, index) => {
        const gross = Number(item.gross || 0);
        const deduct = Number(item.payroll?.deductionAmount || 0);
        const payable = Number(item.payroll?.payableAmount || 0);

        totalGrossSum += gross;
        totalDeductSum += deduct;
        totalPayableSum += payable;

        wsData.push([
          index + 1,
          item.name || "-",
          item.email || "-",
          item.mobile || "-",
          item.level || "Staff",
          item.attendance?.present || 0,
          item.attendance?.halfDay || 0,
          item.attendance?.absent || 0,
          item.attendance?.leave || 0,
          Number(item.payroll?.paidDays || 0),
          gross,
          deduct,
          payable,
          item.salary ? "Configured" : "Pending",
        ]);
      });

      wsData.push([
        "",
        "TOTAL",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        totalGrossSum,
        totalDeductSum,
        totalPayableSum,
        "",
      ]);

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } },
      ];

      ws["!cols"] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 26 },
        { wch: 15 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 12 },
        { wch: 16 },
        { wch: 15 },
        { wch: 16 },
        { wch: 14 },
      ];

      const numCols = 14;
      const totalRowIndex = wsData.length;

      for (let c = 0; c < numCols; c++) {
        const cell1 = XLSX.utils.encode_cell({ r: 0, c });
        const cell2 = XLSX.utils.encode_cell({ r: 1, c });
        if (ws[cell1]) ws[cell1].s = titleStyle;
        if (ws[cell2]) ws[cell2].s = subTitleStyle;
      }

      for (let c = 0; c < 4; c++) {
        const cellH = XLSX.utils.encode_cell({ r: 3, c });
        const cellV = XLSX.utils.encode_cell({ r: 4, c });
        if (ws[cellH]) ws[cellH].s = kpiHeaderStyle;
        if (ws[cellV]) {
          ws[cellV].s = {
            ...kpiValueStyle,
            numFmt: c >= 2 ? "₹#,##0" : "0",
          };
        }
      }

      for (let c = 0; c < numCols; c++) {
        const cell = XLSX.utils.encode_cell({ r: 6, c });
        if (ws[cell]) ws[cell].s = thStyle;
      }

      filteredStaff.forEach((item, i) => {
        const r = 7 + i;
        const isEven = i % 2 === 0;
        const rowBg = isEven ? "FFFFFF" : "F8FAFC";

        for (let c = 0; c < numCols; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (!ws[cell]) continue;

          let align = "center";
          let numFmt = undefined;
          let fontColor = "111827";
          let isBold = false;

          if (c === 1 || c === 2 || c === 3 || c === 4) align = "left";
          else if (c >= 5 && c <= 9) align = "center";
          else if (c >= 10 && c <= 12) {
            align = "right";
            numFmt = "₹#,##0";
            if (c === 12) isBold = true;
          } else if (c === 13) {
            align = "center";
            fontColor = item.salary ? "15803D" : "B45309";
            isBold = true;
          }

          ws[cell].s = {
            font: { name: "Calibri", sz: 10, color: { rgb: fontColor }, bold: isBold },
            fill: { fgColor: { rgb: rowBg } },
            alignment: { horizontal: align, vertical: "center" },
            border: borderThin,
            numFmt,
          };
        }
      });

      const totalR = totalRowIndex - 1;
      for (let c = 0; c < numCols; c++) {
        const cell = XLSX.utils.encode_cell({ r: totalR, c });
        if (!ws[cell]) ws[cell] = { t: "s", v: "" };

        let align = "center";
        let numFmt = undefined;
        if (c === 1) align = "left";
        if (c >= 10 && c <= 12) {
          align = "right";
          numFmt = "₹#,##0";
        }

        ws[cell].s = {
          font: { name: "Calibri", sz: 11, bold: true, color: { rgb: "1E3A8A" } },
          fill: { fgColor: { rgb: "EFF6FF" } },
          alignment: { horizontal: align, vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "1E3A8A" } },
            bottom: { style: "double", color: { rgb: "1E3A8A" } },
            left: borderThin.left,
            right: borderThin.right,
          },
          numFmt,
        };
      }

      XLSX.utils.book_append_sheet(wb, ws, "Salary Overview");
      XLSX.writeFile(wb, `${getExportFileBase()}.xlsx`);
    } catch (error) {
      console.error("Excel export error:", error);
      swal("Error", "Failed to export Excel", "error");
    } finally {
      setExporting("");
    }
  };

  const handleExportPdf = async () => {
    try {
      setExporting("pdf");
      const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const pageSize = [842, 595];
      const margin = 28;
      const rowHeight = 18;

      const columns = [
        { label: "#", key: "#", width: 24, align: "center", max: 3 },
        { label: "Staff Name", key: "Staff", width: 115, align: "left", max: 18 },
        { label: "Contact / Email", key: "Email", width: 130, align: "left", max: 24 },
        { label: "Level", key: "Level", width: 55, align: "center", max: 10 },
        { label: "P", key: "Present", width: 22, align: "center", max: 3 },
        { label: "HD", key: "Half Day", width: 24, align: "center", max: 3 },
        { label: "A", key: "Absent", width: 22, align: "center", max: 3 },
        { label: "PL", key: "Paid Leave", width: 24, align: "center", max: 3 },
        { label: "Paid", key: "Paid Days", width: 32, align: "center", max: 4 },
        { label: "Gross", key: "Gross Salary", width: 85, align: "right", max: 14, money: true },
        { label: "Deduct", key: "Deduction", width: 75, align: "right", max: 12, money: true },
        { label: "Net Payable", key: "Payable", width: 90, align: "right", max: 14, money: true },
        { label: "Status", key: "Status", width: 68, align: "center", max: 12 },
      ];

      const totalTableWidth = columns.reduce((s, c) => s + c.width, 0);

      let page;
      let y;
      let pageNumber = 0;

      const addPage = () => {
        page = pdfDoc.addPage(pageSize);
        pageNumber += 1;
        y = pageSize[1] - margin;
      };

      const draw = (text, x, lineY, options = {}) => {
        page.drawText(safeText(text), {
          x,
          y: lineY,
          size: options.size || 8,
          font: options.bold ? boldFont : font,
          color: options.color || rgb(0.1, 0.12, 0.16),
        });
      };

      const drawHeaderBanner = () => {
        page.drawRectangle({
          x: margin,
          y: y - 26,
          width: pageSize[0] - margin * 2,
          height: 38,
          color: rgb(0.12, 0.23, 0.54),
        });

        draw("CANSCORP CONSTRUCTIONS — STAFF SALARY OVERVIEW", margin + 12, y - 6, {
          bold: true,
          size: 13,
          color: rgb(1, 1, 1),
        });

        draw(
          `Period: ${months[month]} ${year} | Generated: ${new Date().toLocaleDateString("en-IN")}`,
          margin + 12,
          y - 20,
          { size: 8, color: rgb(0.85, 0.9, 0.98) }
        );

        y -= 44;

        page.drawRectangle({
          x: margin,
          y: y - 18,
          width: pageSize[0] - margin * 2,
          height: 26,
          color: rgb(0.96, 0.97, 0.99),
          borderColor: rgb(0.85, 0.89, 0.94),
          borderWidth: 0.8,
        });

        draw(`Total Staff: ${summary.staffCount || filteredStaff.length || 0}`, margin + 14, y - 8, {
          bold: true,
          size: 8.5,
          color: rgb(0.15, 0.2, 0.3),
        });
        draw(`Configured: ${summary.salaryConfigured || 0}`, margin + 140, y - 8, {
          bold: true,
          size: 8.5,
          color: rgb(0.15, 0.2, 0.3),
        });
        draw(`Total Gross: ${formatExportMoney(summary.totalGross)}`, margin + 300, y - 8, {
          bold: true,
          size: 8.5,
          color: rgb(0.12, 0.23, 0.54),
        });
        draw(`Total Payable: ${formatExportMoney(summary.totalPayable)}`, margin + 520, y - 8, {
          bold: true,
          size: 8.5,
          color: rgb(0.08, 0.52, 0.24),
        });

        y -= 30;
      };

      const drawTableHeader = () => {
        let x = margin;
        page.drawRectangle({
          x: margin,
          y: y - 4,
          width: totalTableWidth,
          height: 18,
          color: rgb(0.12, 0.23, 0.54),
        });

        columns.forEach((col) => {
          let textX = x + 4;
          if (col.align === "right") textX = x + col.width - 6;
          else if (col.align === "center") textX = x + (col.width / 2) - 8;

          draw(col.label, textX, y + 2, { bold: true, size: 7.5, color: rgb(1, 1, 1) });
          x += col.width;
        });
        y -= rowHeight;
      };

      const drawFooter = () => {
        page.drawLine({
          start: { x: margin, y: margin + 14 },
          end: { x: pageSize[0] - margin, y: margin + 14 },
          thickness: 0.5,
          color: rgb(0.82, 0.85, 0.9),
        });

        draw("Confidential - Canscorp Staff Payroll Report", margin, margin + 4, {
          size: 7,
          color: rgb(0.5, 0.55, 0.6),
        });

        draw(
          `Page ${pageNumber}`,
          pageSize[0] - margin - 40,
          margin + 4,
          { size: 7, color: rgb(0.5, 0.55, 0.6) }
        );
      };

      addPage();
      drawHeaderBanner();
      drawTableHeader();

      let totalGross = 0;
      let totalDeduct = 0;
      let totalPayable = 0;

      exportRows.forEach((row, rowIndex) => {
        if (y < margin + 40) {
          drawFooter();
          addPage();
          drawTableHeader();
        }

        totalGross += Number(row["Gross Salary"] || 0);
        totalDeduct += Number(row["Deduction"] || 0);
        totalPayable += Number(row["Payable"] || 0);

        const isEven = rowIndex % 2 === 0;
        if (!isEven) {
          page.drawRectangle({
            x: margin,
            y: y - 4,
            width: totalTableWidth,
            height: rowHeight,
            color: rgb(0.97, 0.98, 1.0),
          });
        }

        let x = margin;
        columns.forEach((col) => {
          const raw = col.money ? formatExportMoney(row[col.key]) : String(row[col.key] ?? "-");
          const truncated = truncateText(raw, col.max);

          let textX = x + 4;
          if (col.align === "right") textX = x + col.width - 6;
          else if (col.align === "center") textX = x + (col.width / 2) - 8;

          let textColor = rgb(0.12, 0.15, 0.2);
          if (col.key === "Status") {
            textColor = row.Status === "Configured" ? rgb(0.08, 0.52, 0.24) : rgb(0.72, 0.42, 0.05);
          }

          draw(truncated, textX, y, { size: 7, color: textColor, bold: col.key === "Payable" });
          x += col.width;
        });

        page.drawLine({
          start: { x: margin, y: y - 4 },
          end: { x: margin + totalTableWidth, y: y - 4 },
          thickness: 0.3,
          color: rgb(0.88, 0.9, 0.94),
        });

        y -= rowHeight;
      });

      if (y < margin + 30) {
        drawFooter();
        addPage();
        drawTableHeader();
      }

      page.drawRectangle({
        x: margin,
        y: y - 4,
        width: totalTableWidth,
        height: rowHeight + 2,
        color: rgb(0.92, 0.95, 1.0),
      });

      draw("TOTAL", margin + 30, y + 1, { bold: true, size: 8, color: rgb(0.12, 0.23, 0.54) });
      draw(formatExportMoney(totalGross), margin + 355, y + 1, { bold: true, size: 7.5, color: rgb(0.12, 0.23, 0.54) });
      draw(formatExportMoney(totalDeduct), margin + 440, y + 1, { bold: true, size: 7.5, color: rgb(0.12, 0.23, 0.54) });
      draw(formatExportMoney(totalPayable), margin + 530, y + 1, { bold: true, size: 8, color: rgb(0.08, 0.52, 0.24) });

      drawFooter();

      const bytes = await pdfDoc.save();
      downloadBlob(new Blob([bytes], { type: "application/pdf" }), `${getExportFileBase()}.pdf`);
    } catch (error) {
      console.error("PDF export error:", error);
      swal("Error", "Failed to export PDF", "error");
    } finally {
      setExporting("");
    }
  };

  const openLocationExportModal = (targetStaff = null) => {
    const todayStr = new Date().toISOString().split("T")[0];
    setLocationExportConfig({
      scope: "TODAY",
      fromDate: todayStr,
      toDate: todayStr,
      staffId: targetStaff ? String(targetStaff._id) : "",
      includePunchIn: true,
      includePunchOut: true,
      includeGps: true,
    });
    setShowLocationExportModal(true);
  };

  const executeLocationExcelExport = async () => {
    try {
      setExporting("location");
      const XLSX = await import("xlsx-js-style");
      const wb = XLSX.utils.book_new();

      const { scope, fromDate, toDate, staffId, includePunchIn, includePunchOut, includeGps } = locationExportConfig;
      const todayStr = new Date().toISOString().split("T")[0];

      let effectiveFrom = fromDate;
      let effectiveTo = toDate;
      let scopeLabel = "";

      if (scope === "TODAY") {
        effectiveFrom = todayStr;
        effectiveTo = todayStr;
        scopeLabel = `Today (${todayStr})`;
      } else if (scope === "RANGE") {
        scopeLabel = `${effectiveFrom} to ${effectiveTo}`;
      } else if (scope === "MONTH") {
        const m = String(month + 1).padStart(2, "0");
        effectiveFrom = `${year}-${m}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        effectiveTo = `${year}-${m}-${String(lastDay).padStart(2, "0")}`;
        scopeLabel = `${months[month]} ${year} (Full Month)`;
      }

      const targetStaff = staffId ? staff.find((s) => String(s._id) === String(staffId)) : null;

      // 1. Fetch live GPS route tracking logs from backend
      let gpsLogs = [];
      if (includeGps) {
        try {
          const params = { fromDate: effectiveFrom, toDate: effectiveTo };
          if (targetStaff) {
            const uId = targetStaff.userId?._id || targetStaff.userId;
            if (uId) params.userId = uId;
          }
          const logsRes = await api.get("/user-tracking/logs", { params });
          if (logsRes.data?.success) {
            gpsLogs = logsRes.data.data || [];
          }
        } catch (err) {
          console.warn("Could not fetch GPS tracking logs:", err);
        }
      }

      // 2. Fetch or filter punch records for the date range
      let punchList = punches;
      if (scope === "RANGE" || scope === "TODAY") {
        try {
          const pRes = await api.get("/staff-attendance/punches", {
            params: { fromDate: effectiveFrom, toDate: effectiveTo },
          });
          if (pRes.data?.success && Array.isArray(pRes.data.data)) {
            punchList = pRes.data.data;
          }
        } catch (_) { }
      }

      // Filter punches by date and staff
      let relevantPunches = punchList.filter((p) => {
        const d = p.dateKey;
        if (!d) return false;
        if (effectiveFrom && d < effectiveFrom) return false;
        if (effectiveTo && d > effectiveTo) return false;
        return true;
      });

      if (targetStaff) {
        const matchIds = new Set(
          [targetStaff._id, targetStaff.userId?._id, targetStaff.userId]
            .filter(Boolean)
            .map((id) => String(id))
        );
        relevantPunches = relevantPunches.filter(
          (p) => matchIds.has(getEmployeeId(p)) || matchIds.has(getPunchUserId(p))
        );
      }

      // Staff mapping
      const staffMap = new Map();
      staff.forEach((st) => {
        staffMap.set(String(st._id), st);
        if (st.userId?._id) staffMap.set(String(st.userId._id), st);
      });

      const traces = [];

      // Process Punch Locations
      relevantPunches.forEach((p) => {
        const empId = getEmployeeId(p);
        const uId = getPunchUserId(p);
        const st = staffMap.get(empId) || staffMap.get(uId) || {};
        const staffName = st.name || p.userId?.name || "Staff";
        const staffEmail = st.email || p.userId?.email || "-";
        const staffMobile = st.mobile || p.userId?.mobile || "-";

        if (includePunchIn && p.punchInAt && p.locationIn?.latitude && p.locationIn?.longitude) {
          const inDate = new Date(p.punchInAt);
          traces.push({
            timestamp: inDate,
            dateStr: p.dateKey || inDate.toISOString().split("T")[0],
            timeStr: inDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
            staffName,
            email: staffEmail,
            mobile: staffMobile,
            type: "Punch In",
            typeCode: "IN",
            latitude: Number(p.locationIn.latitude),
            longitude: Number(p.locationIn.longitude),
            address: p.locationIn.address || "Office / Site Geofence",
            accuracy: p.locationIn.accuracy ? Number(p.locationIn.accuracy) : null,
            speed: null,
            battery: null,
            remarks: p.remarks || `Status: ${p.status || "PRESENT"}`,
          });
        }

        if (includePunchOut && p.punchOutAt && p.locationOut?.latitude && p.locationOut?.longitude) {
          const outDate = new Date(p.punchOutAt);
          traces.push({
            timestamp: outDate,
            dateStr: p.dateKey || outDate.toISOString().split("T")[0],
            timeStr: outDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
            staffName,
            email: staffEmail,
            mobile: staffMobile,
            type: "Punch Out",
            typeCode: "OUT",
            latitude: Number(p.locationOut.latitude),
            longitude: Number(p.locationOut.longitude),
            address: p.locationOut.address || "Office / Site Geofence",
            accuracy: p.locationOut.accuracy ? Number(p.locationOut.accuracy) : null,
            speed: null,
            battery: null,
            remarks: p.remarks || `Duration: ${formatDuration(p.totalMinutes)}`,
          });
        }
      });

      // Process GPS Tracking Logs
      if (includeGps) {
        gpsLogs.forEach((log) => {
          const u = log.user || {};
          const staffName = u.name || "Staff";
          const staffEmail = u.email || "-";
          const staffMobile = u.mobile || "-";
          const recDate = log.recordedAt ? new Date(log.recordedAt) : new Date();

          traces.push({
            timestamp: recDate,
            dateStr: log.dateKey || recDate.toISOString().split("T")[0],
            timeStr: recDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
            staffName,
            email: staffEmail,
            mobile: staffMobile,
            type: "GPS Live Tracking",
            typeCode: "GPS",
            latitude: Number(log.latitude),
            longitude: Number(log.longitude),
            address: log.address || "Field Location Trace",
            accuracy: log.accuracy ? Number(log.accuracy) : null,
            speed: log.speed !== null && log.speed !== undefined ? Number(log.speed) : null,
            battery: log.battery !== null && log.battery !== undefined ? Number(log.battery) : null,
            remarks: log.heading ? `Heading: ${log.heading}°` : "Movement Log",
          });
        });
      }

      traces.sort((a, b) => b.timestamp - a.timestamp);

      if (traces.length === 0) {
        swal("Notice", `No location tracking traces found for ${scopeLabel}`, "info");
        setExporting("");
        return;
      }

      // 3. Resolve addresses from lat/long coordinates using Google Maps / reverse geocoding
      const pointsToGeocode = traces
        .filter((tr) => Number.isFinite(tr.latitude) && Number.isFinite(tr.longitude))
        .map((tr) => ({ lat: tr.latitude, lng: tr.longitude }));

      if (pointsToGeocode.length > 0) {
        try {
          const geoRes = await api.post("/user-tracking/reverse-geocode", { points: pointsToGeocode });
          if (geoRes.data?.success && geoRes.data?.data) {
            const addrMap = geoRes.data.data;
            traces.forEach((tr) => {
              const key = `${Number(tr.latitude).toFixed(4)},${Number(tr.longitude).toFixed(4)}`;
              if (addrMap[key]) {
                tr.address = addrMap[key];
              }
            });
          }
        } catch (geoErr) {
          console.warn("Reverse geocoding error during export:", geoErr);
        }
      }

      const borderThin = {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "thin", color: { rgb: "D1D5DB" } },
      };

      const titleStyle = {
        font: { name: "Calibri", sz: 14, bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "0F766E" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const subTitleStyle = {
        font: { name: "Calibri", sz: 10, italic: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "0D9488" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const kpiHeaderStyle = {
        font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "374151" } },
        fill: { fgColor: { rgb: "F1F5F9" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderThin,
      };

      const kpiValueStyle = {
        font: { name: "Calibri", sz: 14, bold: true, color: { rgb: "0F766E" } },
        fill: { fgColor: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderThin,
      };

      const thStyle = {
        font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "0F766E" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: borderThin,
      };

      const totalIn = traces.filter((t) => t.typeCode === "IN").length;
      const totalOut = traces.filter((t) => t.typeCode === "OUT").length;
      const totalGps = traces.filter((t) => t.typeCode === "GPS").length;
      const exportDateStr = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

      const wsData = [
        [`CANSCORP CONSTRUCTIONS — STAFF LOCATION & GPS TRACKING LOGS`],
        [`Filter: ${scopeLabel} | Staff: ${targetStaff ? targetStaff.name : "All Staff"} | Generated: ${exportDateStr}`],
        [],
        [
          "Total Location Traces", "", "", "",
          "Punch In Points", "", "",
          "Punch Out Points", "", "", "",
          "GPS Route Tracking Points", "", "", ""
        ],
        [
          traces.length, "", "", "",
          totalIn, "", "",
          totalOut, "", "", "",
          totalGps, "", "", ""
        ],
        [],
        [
          "#",
          "Date",
          "Time (IST)",
          "Staff Name",
          "Email",
          "Mobile",
          "Trace Event",
          "Latitude",
          "Longitude",
          "Location Address / Tag",
          "Accuracy (m)",
          "Speed (km/h)",
          "Battery (%)",
          "Google Maps Link",
          "Remarks",
        ],
      ];

      traces.forEach((tr, index) => {
        const mapUrl = `https://www.google.com/maps?q=${tr.latitude},${tr.longitude}`;
        wsData.push([
          index + 1,
          tr.dateStr,
          tr.timeStr,
          tr.staffName,
          tr.email,
          tr.mobile,
          tr.type,
          tr.latitude,
          tr.longitude,
          tr.address,
          tr.accuracy !== null ? tr.accuracy : "-",
          tr.speed !== null ? tr.speed : "-",
          tr.battery !== null ? `${tr.battery}%` : "-",
          mapUrl,
          tr.remarks,
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 14 } },

        // KPI Card 1: Total Location Traces
        { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
        { s: { r: 4, c: 0 }, e: { r: 4, c: 3 } },

        // KPI Card 2: Punch In Points
        { s: { r: 3, c: 4 }, e: { r: 3, c: 6 } },
        { s: { r: 4, c: 4 }, e: { r: 4, c: 6 } },

        // KPI Card 3: Punch Out Points
        { s: { r: 3, c: 7 }, e: { r: 3, c: 10 } },
        { s: { r: 4, c: 7 }, e: { r: 4, c: 10 } },

        // KPI Card 4: GPS Route Tracking Points
        { s: { r: 3, c: 11 }, e: { r: 3, c: 14 } },
        { s: { r: 4, c: 11 }, e: { r: 4, c: 14 } },
      ];

      ws["!rows"] = [
        { hpt: 26 },
        { hpt: 18 },
        { hpt: 10 },
        { hpt: 22 },
        { hpt: 28 },
        { hpt: 12 },
        { hpt: 24 },
      ];

      ws["!cols"] = [
        { wch: 6 },
        { wch: 13 },
        { wch: 14 },
        { wch: 22 },
        { wch: 26 },
        { wch: 15 },
        { wch: 18 },
        { wch: 14 },
        { wch: 14 },
        { wch: 32 },
        { wch: 13 },
        { wch: 13 },
        { wch: 12 },
        { wch: 42 },
        { wch: 22 },
      ];

      const numCols = 15;

      for (let c = 0; c < numCols; c++) {
        const cell1 = XLSX.utils.encode_cell({ r: 0, c });
        const cell2 = XLSX.utils.encode_cell({ r: 1, c });
        if (ws[cell1]) ws[cell1].s = titleStyle;
        if (ws[cell2]) ws[cell2].s = subTitleStyle;
      }

      for (let c = 0; c < numCols; c++) {
        const cellH = XLSX.utils.encode_cell({ r: 3, c });
        const cellV = XLSX.utils.encode_cell({ r: 4, c });
        if (!ws[cellH]) ws[cellH] = { t: "s", v: "" };
        if (!ws[cellV]) ws[cellV] = { t: "s", v: "" };
        ws[cellH].s = kpiHeaderStyle;
        ws[cellV].s = kpiValueStyle;
      }

      for (let c = 0; c < numCols; c++) {
        const cell = XLSX.utils.encode_cell({ r: 6, c });
        if (ws[cell]) ws[cell].s = thStyle;
      }

      traces.forEach((tr, i) => {
        const r = 7 + i;
        const isEven = i % 2 === 0;
        const rowBg = isEven ? "FFFFFF" : "F8FAFC";

        let eventColor = "0F766E";
        if (tr.typeCode === "IN") eventColor = "1D4ED8";
        else if (tr.typeCode === "OUT") eventColor = "7E22CE";
        else if (tr.typeCode === "GPS") eventColor = "059669";

        for (let c = 0; c < numCols; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (!ws[cell]) continue;

          let align = "center";
          let fontColor = "111827";
          let isBold = false;

          if (c === 3 || c === 4 || c === 5 || c === 9 || c === 14) align = "left";
          else if (c === 7 || c === 8 || c === 10 || c === 11) align = "right";
          else if (c === 13) align = "left";

          if (c === 6) {
            fontColor = eventColor;
            isBold = true;
          }

          if (c === 13) {
            fontColor = "2563EB";
          }

          ws[cell].s = {
            font: { name: "Calibri", sz: 9.5, color: { rgb: fontColor }, bold: isBold },
            fill: { fgColor: { rgb: rowBg } },
            alignment: { horizontal: align, vertical: "center" },
            border: borderThin,
          };
        }
      });

      const fileSuffix = targetStaff ? `_${sanitizeFileName(targetStaff.name)}` : "";
      const datePart = sanitizeFileName(scopeLabel);
      XLSX.utils.book_append_sheet(wb, ws, "Location Tracking");
      XLSX.writeFile(wb, `${sanitizeFileName(`Staff_Location_Tracking_${datePart}${fileSuffix}`)}.xlsx`);
      setShowLocationExportModal(false);
    } catch (error) {
      console.error("Location Excel export error:", error);
      swal("Error", "Failed to export location tracking Excel", "error");
    } finally {
      setExporting("");
    }
  };

  const countStatus = (employeeId, status) => (attendance[employeeId] || []).filter((item) => item === status).length;
  const getPunch = (employeeId, dayIndex) => punchMap[`${employeeId}-${dayIndex}`];

  const selectedPunches = useMemo(() => {
    if (!selectedViewStaff) return [];

    const matchIds = new Set(
      [selectedViewStaff._id, selectedViewStaff.userId?._id, selectedViewStaff.userId]
        .filter(Boolean)
        .map((id) => String(id))
    );

    return punches
      .filter((record) => matchIds.has(getEmployeeId(record)) || matchIds.has(getPunchUserId(record)))
      .filter((record) => !logDay || Number((record.dateKey || "").split("-")[2]) === Number(logDay))
      .sort((a, b) => (a.dateKey || "").localeCompare(b.dateKey || ""));
  }, [logDay, punches, selectedViewStaff]);

  const selectedTotalMinutes = selectedPunches.reduce((sum, record) => sum + Number(record.totalMinutes || 0), 0);
  const renderSummaryBadge = (item, status, label) => (
    <Badge bg={gridStatusVariant[status]} className="me-1 mb-1">{label}: {countStatus(item._id, status)}</Badge>
  );

  return (
    <Fragment>
      <PageTitle activeMenu="Staff Salary" motherMenu="HR" />

      <Row className="g-3 mb-3">
        <Col md={3}><Card className="h-100"><Card.Body><div className="text-muted small">Staff Count</div><h4 className="mb-0">{summary.staffCount || 0}</h4></Card.Body></Card></Col>
        <Col md={3}><Card className="h-100"><Card.Body><div className="text-muted small">Salary Configured</div><h4 className="mb-0">{summary.salaryConfigured || 0}</h4></Card.Body></Card></Col>
        <Col md={3}><Card className="h-100"><Card.Body><div className="text-muted small">Total Gross</div><h4 className="mb-0">{formatMoney(summary.totalGross)}</h4></Card.Body></Card></Col>
        <Col md={3}><Card className="h-100"><Card.Body><div className="text-muted small">Total Payable</div><h4 className="mb-0">{formatMoney(summary.totalPayable)}</h4></Card.Body></Card></Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <Card.Title>Staff Salary Overview</Card.Title>
            <div className="text-muted small">Office {config.officeStartTime} - {config.officeEndTime} | Full {config.fullDayMinutes} min | Half {config.halfDayMinutes} min</div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="success" className="d-flex align-items-center gap-1 shadow-sm" onClick={() => navigate("/live-tracking")}>
              Live Tracking
            </Button>
            <Button variant="outline-primary" onClick={() => navigate("/staff-attendance")}>Staff Attendance</Button>
            <Button variant="outline-primary" onClick={() => setShowConfig(true)}>Attendance Settings</Button>
            <Button variant="primary" onClick={openEnroll}>Enroll Staff</Button>
          </div>
        </Card.Header>

        <Card.Body>
          <Row className="g-2 mb-3 align-items-end staff-salary-filter-row">
            <Col lg="auto" md={3} className="staff-salary-filter-month">
              <Form.Label>Month</Form.Label>
              <Form.Select className="staff-salary-filter-control" value={month} onChange={(e) => { const nextMonth = Number(e.target.value); setMonth(nextMonth); setLogDay(getDefaultLogDay(nextMonth, year)); }}>
                {months.map((item, index) => <option key={item} value={index}>{item}</option>)}
              </Form.Select>
            </Col>
            <Col lg="auto" md={2} className="staff-salary-filter-year">
              <Form.Label>Year</Form.Label>
              <Form.Select className="staff-salary-filter-control" value={year} onChange={(e) => { const nextYear = Number(e.target.value); setYear(nextYear); setLogDay(getDefaultLogDay(month, nextYear)); }}>
                {[2024, 2025, 2026, 2027, 2028].map((item) => <option key={item} value={item}>{item}</option>)}
              </Form.Select>
            </Col>
            <Col lg="auto" md={4} className="staff-salary-filter-search">
              <Form.Label>Search</Form.Label>
              <Form.Control className="staff-salary-filter-control" placeholder="Search staff" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadOverview()} />
            </Col>
            <Col lg md={12} className="staff-salary-filter-actions d-flex gap-2 flex-wrap justify-content-lg-end">
              <Button variant="outline-danger" className="staff-salary-filter-btn px-3" onClick={handleExportPdf} disabled={loading || exporting === "pdf"}>
                {exporting === "pdf" ? "Exporting..." : "Export PDF"}
              </Button>
              <Button variant="outline-success" className="staff-salary-filter-btn px-3" onClick={handleExportExcel} disabled={loading || exporting === "excel"}>
                {exporting === "excel" ? "Exporting..." : "Export Excel"}
              </Button>
            </Col>
          </Row>

          {loading ? <div className="text-center py-5"><Spinner /></div> : (
            <Table bordered hover responsive className="align-middle">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 44 }}>#</th>
                  <th className="text-center">Staff</th>
                  <th className="text-center">Contact</th>
                  <th className="text-center">Attendance</th>
                  <th className="text-center">Gross Salary</th>
                  <th className="text-center">Payable</th>
                  <th className="text-center">Status</th>
                  <th className="text-center text-nowrap" style={{ width: 280, minWidth: 280 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.length === 0 ? (
                  <tr><td colSpan="8" className="text-center text-muted py-4">No staff enrolled</td></tr>
                ) : filteredStaff.map((item, index) => (
                  <tr key={item._id}>
                    <td className="text-center text-muted small">{index + 1}</td>
                    <td className="text-center"><strong>{item.name}</strong><div className="text-muted small">{item.level || "Staff"}</div></td>
                    <td className="text-center"><div>{item.email || "-"}</div><div className="text-muted small">{item.mobile || "-"}</div></td>
                    <td className="text-center small">
                      <span className="me-1"><span className="text-success fw-semibold">P:</span> {item.attendance?.present || 0}</span>
                      <span className="me-1"><span className="text-warning fw-semibold">HD:</span> {item.attendance?.halfDay || 0}</span>
                      <span className="me-1"><span className="text-danger fw-semibold">A:</span> {item.attendance?.absent || 0}</span>
                      <span><span className="text-info fw-semibold">PL:</span> {item.attendance?.leave || 0}</span>
                    </td>
                    <td className="text-center">{formatMoney(item.gross)}</td>
                    <td className="text-center">
                      <strong>{formatMoney(item.payroll?.totalPayableWithReimbursement || item.payroll?.payableAmount)}</strong>
                      {Number(item.payroll?.reimbursementAmount || 0) > 0 && (
                        <div className="text-success small fw-semibold">
                          + Reimb: {formatMoney(item.payroll.reimbursementAmount)}
                        </div>
                      )}
                      <div className="text-muted small">Paid {item.payroll?.paidDays || 0}/{item.payroll?.salaryDays || 0}{item.payroll?.deductionAmount ? ` | Deduct ${formatMoney(item.payroll.deductionAmount)}` : ""}</div>
                    </td>
                    <td className="text-center">
                      <Badge bg={item.salary ? "success" : "warning"} text={item.salary ? undefined : "dark"}>{item.salary ? "Configured" : "Pending"}</Badge>
                    </td>
                    <td className="text-center text-nowrap" style={{ minWidth: 280 }}>
                      <div className="d-flex gap-1 align-items-center justify-content-center flex-nowrap">
                        <Button size="sm" variant="outline-primary" onClick={() => openAttendanceView(item)}>View</Button>
                        <Button size="sm" variant="primary" onClick={() => openPayrollEdit(item)}>Edit</Button>
                        {(() => {
                          const pr = processedMap[String(item._id)];
                          if (pr?.status === "PAID") {
                            return <Badge bg="success" className="d-inline-flex align-items-center justify-content-center px-2" style={{ height: 31, fontSize: "0.78rem" }}>Paid</Badge>;
                          }
                          if (pr?.status === "PENDING") {
                            return <Badge bg="warning" text="dark" className="d-inline-flex align-items-center justify-content-center px-2" style={{ height: 31, fontSize: "0.78rem" }}>Processing</Badge>;
                          }
                          return (
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={processingId === item._id}
                              onClick={() => processSalary(item)}
                            >
                              {processingId === item._id ? "..." : "Process Salary"}
                            </Button>
                          );
                        })()}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{ width: 32, height: 31 }}
                          title="Unenroll from Payroll"
                          onClick={() => unenrollUser(item)}
                        >
                          <i className="fa fa-user-minus"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(selectedViewStaff)} onHide={() => setSelectedViewStaff(null)} size="xl" dialogClassName="staff-attendance-detail-modal">
        <Modal.Header closeButton><Modal.Title>{selectedViewStaff?.name || "Staff"} - {months[month]} {year}</Modal.Title></Modal.Header>
        <Modal.Body style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto", overflowX: "hidden" }}>
          {selectedViewStaff && <Row className="g-3">
            <Col lg={8} className="d-flex flex-column" style={{ minHeight: 0 }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Attendance Calendar</h5>
                  <div className="d-flex flex-wrap gap-1">
                    {renderSummaryBadge(selectedViewStaff, "P", "P")}
                    {renderSummaryBadge(selectedViewStaff, "HD", "HD")}
                    {renderSummaryBadge(selectedViewStaff, "A", "A")}
                    {renderSummaryBadge(selectedViewStaff, "L", "PL")}
                    {renderSummaryBadge(selectedViewStaff, "WO", "WO")}
                    {renderSummaryBadge(selectedViewStaff, "H", "H")}
                  </div>
                </div>
                <div className="calendar-filter-controls">
                  <Form.Select size="sm" value={month} onChange={(e) => { const nextMonth = Number(e.target.value); setMonth(nextMonth); setLogDay(getDefaultLogDay(nextMonth, year)); }}>
                    {months.map((item, index) => <option key={item} value={index}>{item}</option>)}
                  </Form.Select>
                  <Form.Select size="sm" value={year} onChange={(e) => { const nextYear = Number(e.target.value); setYear(nextYear); setLogDay(getDefaultLogDay(month, nextYear)); }}>
                    {[2024, 2025, 2026, 2027, 2028].map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </div>
              </div>

              <div className="table-responsive flex-grow-1" style={{ overflowX: "auto" }}>
                <Table bordered className="text-center align-middle mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                  <thead><tr>{weekDays.map((day) => <th key={day.value}>{day.short}</th>)}</tr></thead>
                  <tbody>
                    {Array.from({ length: calendarCells.length / 7 }, (_, rowIndex) => (
                      <tr key={rowIndex}>
                        {calendarCells.slice(rowIndex * 7, rowIndex * 7 + 7).map((day, cellIndex) => {
                          const punch = day !== null ? getPunch(selectedViewStaff._id, day) : null;
                          const isWeekend = day !== null && isWeekendIndex(day);
                          const status = day !== null ? (isWeekend ? "WO" : attendance[selectedViewStaff._id]?.[day] || "") : "";
                          return (
                            <td key={`${rowIndex}-${cellIndex}`} style={{ width: "14.28%", height: 104, padding: 6, verticalAlign: "top" }}>
                              {day !== null && <div className="h-100 d-flex flex-column align-items-start text-start gap-1">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                  <strong>{day + 1}</strong>
                                  <Button size="sm" disabled={isWeekend} variant={status ? gridStatusVariant[status] || "primary" : "outline-secondary"} onClick={() => cycleStatus(selectedViewStaff._id, day)} style={{ minWidth: 38, padding: "0.15rem 0.35rem", fontSize: 11 }}>{status || "-"}</Button>
                                </div>
                                <div className="small text-muted text-truncate w-100">{statusLabels[status] || "Not marked"}</div>
                                {punch && <div className="small text-muted w-100" style={{ lineHeight: 1.2, fontSize: 11 }}><div>In {formatTime(punch.punchInAt)}</div><div>Out {formatTime(punch.punchOutAt)}</div><div>{formatDuration(punch.totalMinutes)}</div></div>}
                              </div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col lg={4} className="staff-attendance-side">
              <div className="staff-summary-panel">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Salary Summary</h5>
                  <Badge bg="light" text="dark">{months[month]}</Badge>
                </div>

                <div className="salary-metric-grid">
                  <div className="salary-metric salary-metric-primary">
                    <span>Net Payable</span>
                    <strong>{formatMoney(selectedViewStaff.payroll?.totalPayableWithReimbursement || selectedViewStaff.payroll?.payableAmount)}</strong>
                    <small>Gross {formatMoney(selectedViewStaff.gross)}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Reimbursement</span>
                    <strong className="text-success">{formatMoney(selectedViewStaff.payroll?.reimbursementAmount || 0)}</strong>
                    <small>{selectedViewStaff.payroll?.reimbursementCount || 0} claims approved</small>
                  </div>
                  <div className="salary-metric">
                    <span>Deduction</span>
                    <strong>{formatMoney(selectedViewStaff.payroll?.deductionAmount)}</strong>
                    <small>Per day {formatMoney(selectedViewStaff.payroll?.perDaySalary)}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Paid Days</span>
                    <strong>{selectedViewStaff.payroll?.paidDays || 0}</strong>
                    <small>Salary days {selectedViewStaff.payroll?.salaryDays || daysInMonth}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Punch Hours</span>
                    <strong>{formatDuration(selectedTotalMinutes)}</strong>
                    <small>{selectedPunches.length} records</small>
                  </div>
                </div>

                {/* Salary Structure */}
                {selectedViewStaff.salary && (
                  <>
                    <div className="detail-section-title mt-3 mb-2">Salary Structure</div>
                    <div className="detail-grid">
                      {[
                        { label: "Basic", value: selectedViewStaff.salary.basic },
                        { label: "DA", value: selectedViewStaff.salary.da },
                        { label: "HRA", value: selectedViewStaff.salary.hra },
                        { label: "Medical", value: selectedViewStaff.salary.medical },
                        { label: "Conveyance", value: selectedViewStaff.salary.conveyance },
                        { label: "Gross", value: selectedViewStaff.gross, highlight: true },
                      ].map(({ label, value, highlight }) => (
                        <div key={label} className={`detail-row ${highlight ? "detail-row-highlight" : ""}`}>
                          <span>{label}</span>
                          <strong>{formatMoney(value || 0)}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Compliance & Deductions */}
                {selectedViewStaff.compliance && (
                  <>
                    <div className="detail-section-title mt-3 mb-2">Compliance / Deductions</div>
                    <div className="detail-grid">
                      {selectedViewStaff.compliance.pfApplicable && (<>
                        <div className="detail-row">
                          <span>PF Employee ({selectedViewStaff.compliance.pfRate || 12}%)</span>
                          <strong>{formatMoney(selectedViewStaff.payroll?.pfEmployeeDeduction || 0)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>PF Employer ({selectedViewStaff.compliance.pfRate || 12}%)</span>
                          <strong className="text-muted">{formatMoney(selectedViewStaff.payroll?.pfEmployerDeduction || 0)}</strong>
                        </div>
                        {selectedViewStaff.compliance.uan && (
                          <div className="detail-row">
                            <span>UAN</span>
                            <strong>{selectedViewStaff.compliance.uan}</strong>
                          </div>
                        )}
                        {selectedViewStaff.compliance.pfNumber && (
                          <div className="detail-row">
                            <span>PF Number</span>
                            <strong>{selectedViewStaff.compliance.pfNumber}</strong>
                          </div>
                        )}
                      </>)}
                      {selectedViewStaff.compliance.esicApplicable && (<>
                        <div className="detail-row">
                          <span>ESIC ({selectedViewStaff.compliance.esicRate || 0.75}%)</span>
                          <strong>{formatMoney(selectedViewStaff.payroll?.esicDeduction || 0)}</strong>
                        </div>
                        {selectedViewStaff.compliance.esicNumber && (
                          <div className="detail-row">
                            <span>ESIC Number</span>
                            <strong>{selectedViewStaff.compliance.esicNumber}</strong>
                          </div>
                        )}
                      </>)}
                      {selectedViewStaff.compliance.ptApplicable && (
                        <div className="detail-row">
                          <span>PT ({selectedViewStaff.compliance.ptState || ""})</span>
                          <strong>{formatMoney(selectedViewStaff.payroll?.ptDeduction || 0)}</strong>
                        </div>
                      )}
                      {selectedViewStaff.compliance.tdsApplicable && (
                        <div className="detail-row">
                          <span>TDS {selectedViewStaff.compliance.tdsAmount ? `(₹${selectedViewStaff.compliance.tdsAmount}/mo)` : selectedViewStaff.compliance.tdsRate ? `(${selectedViewStaff.compliance.tdsRate}%)` : ""}</span>
                          <strong>{formatMoney(selectedViewStaff.payroll?.tdsDeduction || 0)}</strong>
                        </div>
                      )}
                      {!selectedViewStaff.compliance.pfApplicable &&
                        !selectedViewStaff.compliance.esicApplicable &&
                        !selectedViewStaff.compliance.ptApplicable &&
                        !selectedViewStaff.compliance.tdsApplicable && (
                          <div className="text-muted small">No compliance configured</div>
                        )}
                    </div>
                  </>
                )}
              </div>

              <div className="punch-log-panel">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div>
                    <h5 className="mb-0">Punch Logs</h5>
                    <span className="text-muted small">{selectedPunches.length} entries</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Select size="sm" value={logDay} onChange={(e) => setLogDay(e.target.value)} style={{ width: 110 }}>
                      <option value="">All Days</option>
                      {Array.from({ length: daysInMonth }, (_, index) => index + 1).map((item) => (
                        <option key={item} value={item}>{String(item) === getDefaultLogDay(month, year) ? `Today (${item})` : `Day ${item}`}</option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => openLocationExportModal(selectedViewStaff)}
                      disabled={exporting === "location"}
                      style={{ fontSize: 11, padding: "0.22rem 0.55rem", whiteSpace: "nowrap" }}
                      title="Export location traces for this employee"
                    >
                      {exporting === "location" ? "..." : "Export Excel"}
                    </Button>
                  </div>
                </div>

                <div className="punch-log-list">
                  {selectedPunches.length === 0 ? (
                    <div className="punch-empty-state">No punch records found</div>
                  ) : selectedPunches.map((record) => (
                    <div className="punch-log-item" key={record._id || `${record.dateKey}-${record.punchInAt}`}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong>{formatDate(record.dateKey)}</strong>
                          <div className="text-muted small">{formatDuration(record.totalMinutes)}</div>
                        </div>
                        <Badge bg={punchStatusVariant[record.status] || "secondary"}>{(record.status || "PRESENT").replace(/_/g, " ")}</Badge>
                      </div>
                      <div className="punch-time-grid">
                        <div><span>In</span><strong>{formatTime(record.punchInAt)}</strong></div>
                        <div><span>Out</span><strong>{formatTime(record.punchOutAt)}</strong></div>
                      </div>
                      <div className="punch-location-box">
                        <div><span>In location</span><strong>{formatLocation(record.locationIn)}</strong></div>
                        <div><span>Out location</span><strong>{formatLocation(record.locationOut)}</strong></div>
                        {(record.locationIn?.latitude || record.locationOut?.latitude) && (
                          <button
                            className="punch-map-btn"
                            onClick={() => setMapRecord(record)}
                          >
                            View Map
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map Preview Modal — renders inside staff detail modal's body */}
              <MapPreviewModal
                show={Boolean(mapRecord)}
                onHide={() => setMapRecord(null)}
                record={mapRecord}
              />
            </Col>
          </Row>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedViewStaff(null)}>Close</Button>
          <Button variant="success" disabled={saving} onClick={saveSelectedAttendance}>{saving ? "Saving..." : "Save Attendance"}</Button>
        </Modal.Footer>
      </Modal> <Modal show={showConfig} onHide={() => setShowConfig(false)} size="lg" enforceFocus={false}>
        <Modal.Header closeButton><Modal.Title>Attendance Settings</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Label>Office Start Time</Form.Label><Form.Control type="time" value={config.officeStartTime || ""} onChange={(e) => setConfig({ ...config, officeStartTime: e.target.value })} /></Col>
            <Col md={6}><Form.Label>Office End Time</Form.Label><Form.Control type="time" value={config.officeEndTime || ""} onChange={(e) => setConfig({ ...config, officeEndTime: e.target.value })} /></Col>
            <Col md={4}><Form.Label>Grace Minutes</Form.Label><Form.Control type="number" value={config.graceMinutes} onChange={(e) => setConfig({ ...config, graceMinutes: Number(e.target.value) })} /></Col>
            <Col md={4}><Form.Label>Full Day Minutes</Form.Label><Form.Control type="number" value={config.fullDayMinutes} onChange={(e) => setConfig({ ...config, fullDayMinutes: Number(e.target.value) })} /></Col>
            <Col md={4}><Form.Label>Half Day Minutes</Form.Label><Form.Control type="number" value={config.halfDayMinutes} onChange={(e) => setConfig({ ...config, halfDayMinutes: Number(e.target.value) })} /></Col>
            <Col md={6}><Form.Label>Salary Calculation Basis</Form.Label><Form.Select value={config.salaryCalculationBasis} onChange={(e) => setConfig({ ...config, salaryCalculationBasis: e.target.value })}><option value="CALENDAR_DAYS">Calendar days</option><option value="WORKING_DAYS">Working days excluding weekends</option><option value="RECORDED_DAYS">Only recorded attendance days</option></Form.Select></Col>
            <Col md={6}><Form.Label>Half Day Pay Ratio</Form.Label><Form.Control type="number" step="0.1" min="0" max="1" value={config.halfDayPayRatio} onChange={(e) => setConfig({ ...config, halfDayPayRatio: Number(e.target.value) })} /></Col>
            <Col md={12}><Form.Label>Weekend Days</Form.Label><div className="d-flex flex-wrap gap-3">{weekDays.map((day) => <Form.Check key={day.value} type="checkbox" label={day.label} checked={(config.weekendDays || []).map(Number).includes(day.value)} onChange={() => toggleWeekend(day.value)} />)}</div></Col>
            <Col md={12}><hr className="my-1" /></Col>
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                  <Form.Label className="mb-0 fw-bold">Approved Office & Site Locations (Geofencing)</Form.Label>
                  <div className="text-muted small">
                    Employees can punch in/out if they are within the allowed radius of ANY approved location.
                  </div>
                </div>
                <Button size="sm" variant="primary" type="button" onClick={addOfficeLocation}>
                  + Add Office
                </Button>
              </div>

              {(!config.officeLocations || config.officeLocations.length === 0) ? (
                <div className="text-muted small p-3 bg-light rounded text-center border">
                  No offices configured. Click "+ Add Office" to register company offices and project sites.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {config.officeLocations.map((office, idx) => (
                    <div
                      key={idx}
                      className="p-3 border rounded position-relative"
                      style={{ background: "rgba(127, 127, 127, 0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="small fw-bold">Office #{idx + 1}</strong>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant={activeMapIndex === idx ? "secondary" : "primary"}
                            className="btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
                            style={{ fontSize: 11, padding: "3px 9px", fontWeight: 600 }}
                            type="button"
                            onClick={() => setActiveMapIndex(activeMapIndex === idx ? null : idx)}
                            title={activeMapIndex === idx ? "Close map" : "Pick location on map"}
                          >
                            {activeMapIndex === idx ? (
                              <>
                                <i className="fas fa-times"></i> Close Map
                              </>
                            ) : (
                              <>
                                <i className="fas fa-map-marker-alt"></i> Set on Map
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="btn-sm d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 11, padding: "3px 9px", fontWeight: 600 }}
                            type="button"
                            disabled={locatingOfficeIndex === idx}
                            onClick={() => handleCaptureLocationForItem(idx)}
                          >
                            {locatingOfficeIndex === idx ? (
                              <>
                                <Spinner animation="border" size="sm" style={{ width: 11, height: 11 }} />
                                <span>Locating...</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-crosshairs"></i>
                                <span>Use My Location</span>
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="btn-sm d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 11, padding: "3px 9px" }}
                            type="button"
                            onClick={() => removeOfficeItem(idx)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>

                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Label className="small mb-1">Office / Site Name</Form.Label>
                          <Form.Control
                            size="sm"
                            value={office.name || ""}
                            onChange={(e) => updateOfficeItem(idx, "name", e.target.value)}
                            placeholder="e.g. Head Office Jaipur"
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Latitude</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="any"
                            value={office.latitude ?? ""}
                            onChange={(e) => updateOfficeItem(idx, "latitude", e.target.value)}
                            placeholder="26.9112"
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Longitude</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="any"
                            value={office.longitude ?? ""}
                            onChange={(e) => updateOfficeItem(idx, "longitude", e.target.value)}
                            placeholder="75.7386"
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Radius (m)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min="10"
                            value={office.allowedRadiusMeters || ""}
                            onChange={(e) => updateOfficeItem(idx, "allowedRadiusMeters", Number(e.target.value || 0))}
                            placeholder="100"
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label className="small mb-1">Address / Landmark (optional)</Form.Label>
                          <Form.Control
                            size="sm"
                            value={office.address || ""}
                            onChange={(e) => updateOfficeItem(idx, "address", e.target.value)}
                            placeholder="Full address or landmark"
                          />
                        </Col>
                      </Row>

                      {/* Inline Expandable Google Map Picker */}
                      {activeMapIndex === idx && (
                        <OfficeGoogleMapPicker
                          office={office}
                          onChange={(updates) => {
                            setConfig((current) => {
                              const list = [...(current.officeLocations || [])];
                              list[idx] = { ...list[idx], ...updates };
                              return { ...current, officeLocations: list };
                            });
                          }}
                          onClose={() => setActiveMapIndex(null)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Col>
            <Col md={6}><Form.Check type="switch" label="Paid Weekend" checked={config.paidWeekend} onChange={(e) => setConfig({ ...config, paidWeekend: e.target.checked })} /><Form.Check type="switch" label="Paid Holiday" checked={config.paidHoliday} onChange={(e) => setConfig({ ...config, paidHoliday: e.target.checked })} /></Col>
            <Col md={6}><Form.Check type="switch" label="Auto Half Day From Punch Hours" checked={config.autoHalfDayFromPunchHours} onChange={(e) => setConfig({ ...config, autoHalfDayFromPunchHours: e.target.checked })} /><Form.Check type="switch" label="Mark Absent If Below Half Day" checked={config.autoAbsentForShortHours} onChange={(e) => setConfig({ ...config, autoAbsentForShortHours: e.target.checked })} /></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowConfig(false)}>Close</Button><Button variant="success" disabled={savingConfig} onClick={saveConfig}>{savingConfig ? "Saving..." : "Save Settings"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showEnroll} onHide={() => { if (!enrollingUserId) setShowEnroll(false); }} size="lg" centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>Enroll Staff</span>
            {!loadingUsers && (
              <Badge bg="primary" pill className="font-w500 ms-1" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>
                {availableUsers.length} Eligible
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {loadingUsers ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
              <div className="mt-3 font-w600">Loading eligible staff...</div>
              <small className="text-muted">Filtering out clients and already enrolled members</small>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <InputGroup size="sm">
                  <InputGroup.Text style={{ background: "transparent" }}>
                    <i className="fas fa-search text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search available staff by name, email, or mobile..."
                    value={enrollSearch}
                    onChange={(e) => setEnrollSearch(e.target.value)}
                  />
                  {enrollSearch && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setEnrollSearch("")}
                      title="Clear search"
                    >
                      <i className="fas fa-times" />
                    </Button>
                  )}
                </InputGroup>
              </div>

              <div className="table-responsive" style={{ maxHeight: "420px" }}>
                <Table bordered hover size="sm" className="mb-0 align-middle">
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ minWidth: 160 }}>NAME</th>
                      <th style={{ minWidth: 180 }}>EMAIL</th>
                      <th style={{ minWidth: 130 }}>MOBILE</th>
                      <th style={{ width: 110, textAlign: "center" }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAvailableUsers.map((user) => (
                      <tr key={user._id}>
                        <td className="fw-semibold">{user.name}</td>
                        <td className="text-muted">{user.email || "-"}</td>
                        <td className="text-muted">{user.mobile || "-"}</td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={Boolean(enrollingUserId) || saving}
                            onClick={() => enrollUser(user)}
                            className="px-3"
                          >
                            {enrollingUserId === user._id ? (
                              <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                <span>Enrolling...</span>
                              </>
                            ) : (
                              "Enroll"
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredAvailableUsers.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          {enrollSearch ? (
                            <div>
                              <div>No staff members match "<strong>{enrollSearch}</strong>"</div>
                              <Button
                                variant="link"
                                size="sm"
                                className="mt-1 p-0 text-decoration-none"
                                onClick={() => setEnrollSearch("")}
                              >
                                Clear search filter
                              </Button>
                            </div>
                          ) : (
                            <div>No available staff members to enroll. All eligible users are already enrolled.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Location Export Modal (Current Day / Date Range / Full Month) */}
      <Modal show={showLocationExportModal} onHide={() => setShowLocationExportModal(false)} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            Export Location Tracking Logs {locationExportConfig.staffId && staff.find((s) => String(s._id) === String(locationExportConfig.staffId)) ? `— ${staff.find((s) => String(s._id) === String(locationExportConfig.staffId)).name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {locationExportConfig.staffId && (
            <div className="mb-3 p-2 rounded bg-info-subtle border border-info d-flex align-items-center justify-content-between">
              <div>
                <div className="small text-muted fw-semibold">STAFF MEMBER</div>
                <strong className="text-dark">
                  {staff.find((s) => String(s._id) === String(locationExportConfig.staffId))?.name || "Selected Staff"}
                </strong>
                <span className="text-muted ms-2 small">
                  ({staff.find((s) => String(s._id) === String(locationExportConfig.staffId))?.email || staff.find((s) => String(s._id) === String(locationExportConfig.staffId))?.mobile || ""})
                </span>
              </div>
              <Badge bg="primary">Single Staff</Badge>
            </div>
          )}
          <div className="mb-3">
            <Form.Label className="fw-semibold small text-muted">SELECT DATE / RANGE</Form.Label>
            <div className="d-flex flex-column gap-2 p-2 bg-light rounded border">
              <Form.Check
                type="radio"
                id="scope-today"
                name="locationScope"
                label={<span><strong>Today (Current Day)</strong> — {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                checked={locationExportConfig.scope === "TODAY"}
                onChange={() => setLocationExportConfig({ ...locationExportConfig, scope: "TODAY" })}
              />
              <Form.Check
                type="radio"
                id="scope-range"
                name="locationScope"
                label={<span><strong>Custom Date Range</strong> (Select From & To Date)</span>}
                checked={locationExportConfig.scope === "RANGE"}
                onChange={() => setLocationExportConfig({ ...locationExportConfig, scope: "RANGE" })}
              />
              <Form.Check
                type="radio"
                id="scope-month"
                name="locationScope"
                label={<span><strong>Full Selected Month</strong> — {months[month]} {year}</span>}
                checked={locationExportConfig.scope === "MONTH"}
                onChange={() => setLocationExportConfig({ ...locationExportConfig, scope: "MONTH" })}
              />
            </div>
          </div>

          {locationExportConfig.scope === "RANGE" && (
            <Row className="g-2 mb-3 p-2 rounded bg-light border">
              <Col xs={6}>
                <Form.Label className="small fw-semibold mb-1">From Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={locationExportConfig.fromDate}
                  onChange={(e) => setLocationExportConfig({ ...locationExportConfig, fromDate: e.target.value })}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="small fw-semibold mb-1">To Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={locationExportConfig.toDate}
                  onChange={(e) => setLocationExportConfig({ ...locationExportConfig, toDate: e.target.value })}
                />
              </Col>
            </Row>
          )}

          <div className="mb-3">
            <Form.Label className="fw-semibold small text-muted">STAFF FILTER</Form.Label>
            <Form.Select
              size="sm"
              value={locationExportConfig.staffId}
              onChange={(e) => setLocationExportConfig({ ...locationExportConfig, staffId: e.target.value })}
            >
              <option value="">All Staff ({staff.length} enrolled)</option>
              {staff.map((st) => (
                <option key={st._id} value={st._id}>
                  {st.name} ({st.email || st.mobile || "Staff"})
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="mb-2">
            <Form.Label className="fw-semibold small text-muted">INCLUDE TRACE TYPES</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              <Form.Check
                type="checkbox"
                id="inc-in"
                label="Punch In"
                checked={locationExportConfig.includePunchIn}
                onChange={(e) => setLocationExportConfig({ ...locationExportConfig, includePunchIn: e.target.checked })}
              />
              <Form.Check
                type="checkbox"
                id="inc-out"
                label="Punch Out"
                checked={locationExportConfig.includePunchOut}
                onChange={(e) => setLocationExportConfig({ ...locationExportConfig, includePunchOut: e.target.checked })}
              />
              <Form.Check
                type="checkbox"
                id="inc-gps"
                label="GPS Route Logs"
                checked={locationExportConfig.includeGps}
                onChange={(e) => setLocationExportConfig({ ...locationExportConfig, includeGps: e.target.checked })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowLocationExportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={exporting === "location"}
            onClick={executeLocationExcelExport}
          >
            {exporting === "location" ? "Generating Excel..." : "Download Excel Report"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
 .staff-salary-filter-control,
 .staff-salary-filter-btn {
 min-height: 48px;
 }
 .staff-salary-filter-month { width: 190px; }
 .staff-salary-filter-year { width: 130px; }
 .staff-salary-filter-search { width: 280px; }
 .staff-salary-filter-actions {
 align-items: end;
 padding-top: 24px;
 }
 @media (max-width: 991.98px) {
 .staff-salary-filter-month,
 .staff-salary-filter-year,
 .staff-salary-filter-search {
 width: 100%;
 }
 .staff-salary-filter-actions {
 padding-top: 0;
 justify-content: flex-start !important;
 }
 }
 .staff-attendance-detail-modal .modal-content { max-height: calc(100vh - 32px); }
 .staff-attendance-detail-modal .modal-body { display: block; }
 .staff-attendance-detail-modal .table th,
 .staff-attendance-detail-modal .table td { word-break: normal; }
 .calendar-filter-controls {
 display: flex;
 gap: 8px;
 align-items: center;
 }
 .calendar-filter-controls .form-select {
 min-width: 120px;
 min-height: 36px;
 border-radius: 8px;
 }
 [data-theme-version="dark"] .calendar-filter-controls .form-select {
 background-color: #111314;
 border-color: #2b2f36;
 color: #ffffff;
 }
 .staff-attendance-side {
 min-height: 0;
 display: flex;
 flex-direction: column;
 gap: 12px;
 }
 .staff-summary-panel,
 .punch-log-panel {
 border: 1px solid #e8edf5;
 border-radius: 12px;
 background: #fff;
 padding: 14px;
 }
 .staff-summary-panel { flex: 0 0 auto; }
 [data-theme-version="dark"] .staff-attendance-detail-modal .modal-content,
 [data-theme-version="dark"] .staff-attendance-detail-modal .modal-header,
 [data-theme-version="dark"] .staff-attendance-detail-modal .modal-body {
 background: #1f1f1f;
 color: #ffffff;
 border-color: #30343b;
 }
 [data-theme-version="dark"] .staff-summary-panel,
 [data-theme-version="dark"] .punch-log-panel {
 background: #1f1f1f;
 border-color: #30343b;
 color: #ffffff;
 }
 [data-theme-version="dark"] .staff-summary-panel .badge.bg-light {
 background-color: #111314 !important;
 border: 1px solid #2b2f36;
 color: #ffffff !important;
 }
 [data-theme-version="dark"] .punch-log-panel .form-select {
 background-color: #111314;
 border-color: #2b2f36;
 color: #ffffff;
 }
 [data-theme-version="dark"] .staff-summary-panel .text-muted,
 [data-theme-version="dark"] .punch-log-panel .text-muted {
 color: #9ca3af !important;
 }
 .punch-log-panel {
 flex: 1 1 auto;
 display: flex;
 flex-direction: column;
 }
 .salary-metric-grid {
 display: grid;
 grid-template-columns: repeat(2, minmax(0, 1fr));
 gap: 10px;
 }
 .salary-metric {
 min-width: 0;
 border: 1px solid #edf1f7;
 border-radius: 10px;
 padding: 10px;
 background: #fbfcff;
 }
 .salary-metric-primary {
 background: #f2f7ff;
 border-color: #d9e8ff;
 }
 [data-theme-version="dark"] .salary-metric {
 background: #151719;
 border-color: #2b2f36;
 }
 [data-theme-version="dark"] .salary-metric-primary {
 background: #17202c;
 border-color: #27374a;
 }
 [data-theme-version="dark"] .salary-metric strong {
 color: #ffffff;
 }
 [data-theme-version="dark"] .salary-metric span,
 [data-theme-version="dark"] .salary-metric small {
 color: #9ca3af;
 }
 .salary-metric span,
 .salary-metric small {
 display: block;
 color: #7d86a2;
 font-size: 12px;
 line-height: 1.25;
 }
 .salary-metric strong {
 display: block;
 color: #111827;
 font-size: 18px;
 line-height: 1.2;
 margin: 4px 0;
 word-break: break-word;
 }
 .punch-log-list {
 overflow-y: auto;
 overflow-x: hidden;
 padding-right: 2px;
 max-height: 500px;
 }
 .punch-log-item {
 border: 1px solid #edf1f7;
 border-radius: 10px;
 padding: 10px;
 background: #fff;
 margin-bottom: 8px;
 }
 [data-theme-version="dark"] .punch-log-item {
 background: #151719;
 border-color: #2b2f36;
 color: #ffffff;
 }
 .punch-time-grid,
 .punch-location-box {
 display: grid;
 grid-template-columns: 1fr 1fr;
 gap: 8px;
 margin-top: 8px;
 }
 .punch-time-grid div,
 .punch-location-box div {
 border-radius: 8px;
 background: #f8fafc;
 padding: 7px 8px;
 }
 .punch-time-grid span,
 .punch-location-box span {
 display: block;
 color: #7d86a2;
 font-size: 11px;
 }
 .punch-time-grid strong,
 .punch-location-box strong {
 color: #111827;
 font-size: 13px;
 word-break: break-word;
 }
 .punch-location-box a {
 grid-column: 1 / -1;
 font-size: 12px;
 }
 .punch-map-btn {
 grid-column: 1 / -1;
 display: inline-flex;
 align-items: center;
 gap: 4px;
 margin-top: 4px;
 padding: 4px 12px;
 font-size: 12px;
 font-weight: 600;
 color: #2563eb;
 background: #eff6ff;
 border: 1px solid #bfdbfe;
 border-radius: 6px;
 cursor: pointer;
 transition: background 0.15s, border-color 0.15s;
 width: fit-content;
 }
 .punch-map-btn:hover {
 background: #dbeafe;
 border-color: #93c5fd;
 }
 [data-theme-version="dark"] .punch-map-btn {
 color: #93c5fd;
 background: #1e2d40;
 border-color: #2563eb;
 }
 [data-theme-version="dark"] .punch-map-btn:hover {
 background: #1d3a5f;
 }
 [data-theme-version="dark"] .punch-time-grid div,
 [data-theme-version="dark"] .punch-location-box div {
 background: #111314;
 }
 [data-theme-version="dark"] .punch-time-grid strong,
 [data-theme-version="dark"] .punch-location-box strong {
 color: #ffffff;
 }
 .punch-empty-state {
 border: 1px dashed #d8dfeb;
 border-radius: 10px;
 color: #7d86a2;
 padding: 24px 12px;
 text-align: center;
 }
 [data-theme-version="dark"] .punch-empty-state {
 border-color: #30343b;
 color: #9ca3af;
 }
 .detail-section-title {
 font-size: 12px;
 font-weight: 700;
 text-transform: uppercase;
 color: #7d86a2;
 letter-spacing: 0.5px;
 border-top: 1px solid #edf1f7;
 padding-top: 10px;
 }
 [data-theme-version="dark"] .detail-section-title {
 color: #9ca3af;
 border-color: #2b2f36;
 }
 .detail-grid {
 display: flex;
 flex-direction: column;
 gap: 4px;
 }
 .detail-row {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 5px 8px;
 border-radius: 6px;
 background: #f8fafc;
 font-size: 13px;
 }
 .detail-row-highlight {
 background: #eef6ff;
 font-weight: 600;
 }
 .detail-row span { color: #6b7280; }
 .detail-row strong { color: #111827; font-size: 13px; }
 [data-theme-version="dark"] .detail-row {
 background: #151719;
 }
 [data-theme-version="dark"] .detail-row-highlight {
 background: #17202c;
 }
 [data-theme-version="dark"] .detail-row span { color: #9ca3af; }
 [data-theme-version="dark"] .detail-row strong { color: #ffffff; }
 @media (max-width: 991.98px) {
 .staff-attendance-side { overflow: visible; }
 .punch-log-list { max-height: 320px; }
 }
 .staff-attendance-detail-modal .modal-content { max-height: calc(100vh - 32px); overflow: hidden; }
 .staff-attendance-detail-modal .modal-body { overflow-y: auto !important; }
 `}</style>

      {/* Map Preview — outside all modals so only one modal shows at a time */}
      <MapPreviewModal
        show={Boolean(mapRecord)}
        onHide={() => setMapRecord(null)}
        record={mapRecord}
      />

      {/* Edit Payroll / Salary Modal */}
      <Modal show={showPayrollModal} onHide={() => setShowPayrollModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payroll Settings — {currentPayrollEmp?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingPayroll ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : (
            <>
              {/* Salary Structure */}
              <h6 className="mb-3 fw-bold text-primary">Salary Structure</h6>
              <Row className="g-2 mb-1">
                {[
                  { key: "basic", label: "Basic" },
                  { key: "da", label: "DA" },
                  { key: "hra", label: "HRA" },
                  { key: "medical", label: "Medical Allowance" },
                  { key: "conveyance", label: "Conveyance Allowance" },
                ].map(({ key, label }) => (
                  <Col md={4} key={key}>
                    <Form.Label className="small mb-1">{label}</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      value={payrollSalary[key] || ""}
                      onChange={(e) => setPayrollSalary({ ...payrollSalary, [key]: e.target.value })}
                    />
                  </Col>
                ))}
                <Col md={4}>
                  <Form.Label className="small mb-1">Gross (auto)</Form.Label>
                  <Form.Control value={`₹${payrollGross.toLocaleString("en-IN")}`} readOnly disabled className="fw-bold" />
                </Col>
              </Row>

              <hr />

              {/* Compliance */}
              <h6 className="mb-3 fw-bold text-primary">Compliance / Deductions</h6>

              {/* PF */}
              <Form.Check
                label="PF Applicable"
                checked={payrollCompliance.pfApplicable}
                onChange={(e) => setPayrollCompliance({ ...payrollCompliance, pfApplicable: e.target.checked })}
              />
              {payrollCompliance.pfApplicable && (
                <Row className="mt-2 g-2 ms-1">
                  <Col md={4}>
                    <Form.Label className="small mb-1">PF Number</Form.Label>
                    <Form.Control
                      placeholder="PF Number"
                      value={payrollCompliance.pfNumber || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, pfNumber: e.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small mb-1">UAN</Form.Label>
                    <Form.Control
                      placeholder="UAN"
                      value={payrollCompliance.uan || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, uan: e.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small mb-1">PF Rate (%)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={payrollCompliance.pfRate ?? 12}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, pfRate: Number(e.target.value) })}
                    />
                    <div className="text-muted" style={{ fontSize: 11 }}>Default 12%</div>
                  </Col>
                </Row>
              )}

              {/* ESIC */}
              <Form.Check
                className="mt-3"
                label="ESIC Applicable"
                checked={payrollCompliance.esicApplicable}
                onChange={(e) => setPayrollCompliance({ ...payrollCompliance, esicApplicable: e.target.checked })}
              />
              {payrollCompliance.esicApplicable && (
                <Row className="mt-2 g-2 ms-1">
                  <Col md={4}>
                    <Form.Label className="small mb-1">ESIC Number</Form.Label>
                    <Form.Control
                      placeholder="ESIC Number"
                      value={payrollCompliance.esicNumber || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, esicNumber: e.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small mb-1">ESIC Rate (%)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={payrollCompliance.esicRate ?? 0.75}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, esicRate: Number(e.target.value) })}
                    />
                    <div className="text-muted" style={{ fontSize: 11 }}>Default 0.75%</div>
                  </Col>
                </Row>
              )}

              {/* PT */}
              <Form.Check
                className="mt-3"
                label="PT Applicable (Professional Tax)"
                checked={payrollCompliance.ptApplicable}
                onChange={(e) => setPayrollCompliance({ ...payrollCompliance, ptApplicable: e.target.checked })}
              />
              {payrollCompliance.ptApplicable && (
                <Row className="mt-2 g-2 ms-1">
                  <Col md={4}>
                    <Form.Label className="small mb-1">State</Form.Label>
                    <Form.Control
                      placeholder="e.g. Maharashtra"
                      value={payrollCompliance.ptState || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, ptState: e.target.value })}
                    />
                  </Col>
                  <Col md={8}>
                    <Form.Label className="small mb-1">PT Slabs</Form.Label>
                    <div className="text-muted small p-2 border rounded bg-light">
                      ≤ ₹7,500 → ₹0 &nbsp;|&nbsp; ≤ ₹10,000 → ₹175 &nbsp;|&nbsp; &gt; ₹10,000 → ₹200
                    </div>
                  </Col>
                </Row>
              )}

              {/* TDS */}
              <Form.Check
                className="mt-3"
                label="TDS Applicable (Tax Deducted at Source)"
                checked={payrollCompliance.tdsApplicable}
                onChange={(e) => setPayrollCompliance({ ...payrollCompliance, tdsApplicable: e.target.checked })}
              />
              {payrollCompliance.tdsApplicable && (
                <div className="mt-2 p-2 border rounded bg-light ms-1">
                  <div className="d-flex gap-3 mb-2">
                    <Form.Check
                      type="radio"
                      id="tdsTypeFixed"
                      name="tdsType"
                      label="Fixed Monthly Amount (₹)"
                      checked={(payrollCompliance.tdsType || "FIXED") === "FIXED"}
                      onChange={() => setPayrollCompliance({ ...payrollCompliance, tdsType: "FIXED", tdsRate: "" })}
                    />
                    <Form.Check
                      type="radio"
                      id="tdsTypePercentage"
                      name="tdsType"
                      label="Percentage of Salary (%)"
                      checked={payrollCompliance.tdsType === "PERCENTAGE"}
                      onChange={() => setPayrollCompliance({ ...payrollCompliance, tdsType: "PERCENTAGE", tdsAmount: "" })}
                    />
                  </div>
                  <Row className="g-2">
                    {(payrollCompliance.tdsType || "FIXED") === "FIXED" ? (
                      <Col md={6}>
                        <Form.Label className="small mb-1">TDS Monthly Amount (₹)</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g. 2000"
                          value={payrollCompliance.tdsAmount || ""}
                          onChange={(e) => setPayrollCompliance({ ...payrollCompliance, tdsAmount: e.target.value, tdsRate: "" })}
                        />
                        <div className="text-muted" style={{ fontSize: 11 }}>Fixed ₹ deduction per month</div>
                      </Col>
                    ) : (
                      <Col md={6}>
                        <Form.Label className="small mb-1">TDS Rate (%)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          placeholder="e.g. 10"
                          value={payrollCompliance.tdsRate || ""}
                          onChange={(e) => setPayrollCompliance({ ...payrollCompliance, tdsRate: e.target.value, tdsAmount: "" })}
                        />
                        <div className="text-muted" style={{ fontSize: 11 }}>% deducted from earned salary</div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {/* WC */}
              <Form.Check
                className="mt-3"
                label="WC Applicable (Workers Compensation)"
                checked={payrollCompliance.wcApplicable}
                onChange={(e) => setPayrollCompliance({ ...payrollCompliance, wcApplicable: e.target.checked })}
              />
              {payrollCompliance.wcApplicable && (
                <Row className="mt-2 g-2 ms-1">
                  <Col md={4}>
                    <Form.Label className="small mb-1">Policy Number</Form.Label>
                    <Form.Control
                      placeholder="Policy Number"
                      value={payrollCompliance.wcPolicyNumber || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, wcPolicyNumber: e.target.value })}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small mb-1">Insurance Vendor</Form.Label>
                    <Form.Control
                      placeholder="Insurance Vendor"
                      value={payrollCompliance.wcInsuranceVendor || ""}
                      onChange={(e) => setPayrollCompliance({ ...payrollCompliance, wcInsuranceVendor: e.target.value })}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayrollModal(false)}>Cancel</Button>
          <Button variant="success" disabled={savingPayroll || loadingPayroll} onClick={handleSavePayroll}>
            {savingPayroll ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default StaffSalaryOverview;






























